import { useEffect, useState } from "react";
import Header from "../../../../Component/RetailerComponents/Header";
import Stepper from "../../../../Component/RetailerComponents/Stepper";
import SuccessSummary from "../../../../Component/RetailerComponents/SuccessSummary";
import FormBody from "../../../../Component/RetailerComponents/FormBody";
import Footer from "../../../../Component/RetailerComponents/Footer";
import toast from "react-hot-toast";
import { APPID, BASE_URL, SecretKey, SWAGGER_URL } from "../../../../config/api";

// Step Labels
const steps = [
  { id: 1, name: "Verify Email & Phone" },
  { id: 2, name: "Payment" },
  { id: 3, name: "PAN Verification" },
  { id: 4, name: "GST Details" },
  { id: 5, name: "Bank Details" },
  { id: 6, name: "Agreement" },
];

export default function RetailerOnboarding() {
  const user = JSON.parse(localStorage.getItem("authUserData") || "{}");

  const isVerified = Number(user?.is_verified || 0);

  // Find next backend step
  const nextStep =
    steps.find(step => step.id > isVerified) ||
    steps[steps.length - 1];

  const initialStep = nextStep.id;


  const [currentStep, setCurrentStep] = useState(initialStep);
  const [submitted, setSubmitted] = useState(isVerified >= steps.length);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [panApiResponse, setPanApiResponse] = useState(null);
  const [aadhaarStatus, setAadhaarStatus] = useState("idle");
  const [esignStatusLoading, setEsignStatusLoading] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);

  const [emailVerified, setEmailVerified] = useState(false);
const [phoneVerified, setPhoneVerified] = useState(false);

  const [gstVerified, setGstVerified] = useState(false);
  const [gstResponse, setGstResponse] = useState(null);

  const [bankVerified, setBankVerified] = useState(false);
  const [bankResponse, setBankResponse] = useState(null);
  const [zohoId, setZohoId] = useState(
  user?.zoho_id || null   // restore if already present
);

  const [form, setForm] = useState({
    email: user?.contact_email || "",
    phone: user?.phone || "",
    paymentRef: "",
    panNumber: "",
    panVerified: false,
    gstTreatment: "",
    gstin: "",
    businessName: "",
    tradeName: "",
    accountNumber: "",
    ifsc: "",
    bankName: "",
    authorisedName: "",
    agreed: false,
    aadhaar: "",          // ✅ Add Aadhaar field
    aadhaarVerified: false, // ✅ Track verification status
  });


  const address_line_1 = user?.address_line1;
  const address_line_2 = user?.address_line2;
  const state = user?.state;
  const district = user?.district;
  const city = user?.city;
  const pincode = user?.pincode;
  const role = user?.role;
  const company_id = user?.company_id;

  const sessionUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

  const isPanVerified =
  form.panVerified === true ||
  panApiResponse !== null ||
  Number(sessionUser?.pan_verified) === 1;

  const isPanAlreadyVerified =
  Number(sessionUser?.pan_verified) === 1 || form.panVerified === true;



// UI-visible steps (renumbered only for display)
const displaySteps =
  role === 5
    ? steps.map((step, index) => ({
        ...step,
        displayIndex: index + 1,
      }))
    : steps
        .filter(step => step.id !== 2) // 🚫 hide payment
        .map((step, index) => ({
          ...step,
          displayIndex: index + 1, // 🔥 UI renumbering
        }));


  const getDisplayStepNumber = (stepId) => {
  if (role === 5) return stepId; // retailer → normal

  // role 2 & 4: payment skipped
  if (stepId > 2) return stepId - 1;

  return stepId;
};
 

  const isUnregistered = form.gstTreatment === "unregistered";

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Simple PAN validation (5 letters, 4 digits, 1 letter)
  const isValidPAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan.toUpperCase());
  };

  const getNextStepFromVerification = (verifiedValue) => {
  const verified = Number(verifiedValue || 0);

  const next =
    steps.find(step => step.id > verified) ||
    steps[steps.length - 1];

  return next.id;
};

useEffect(() => {
  const sessionData = localStorage.getItem("authUserData");
  if (!sessionData) return;

  const user = JSON.parse(sessionData);

  if (user?.is_verified !== undefined) {
    const nextStep = getNextStepFromVerification(user.is_verified);
    setCurrentStep(nextStep);

    if (Number(user.is_verified) >= steps.length) {
      setSubmitted(true);
    }
  }
}, []);



  // ------------------- VALIDATION -------------------
  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!form.email) newErrors.email = "Enter email";
      if (!form.phone) newErrors.phone = "Enter phone number";

      if (!emailVerified) {
        toast.error("Please verify your email before proceeding");
        return false;
      }

      if (!phoneVerified) {
        toast.error("Please verify your phone before proceeding");
        return false;
      }
    }

    if (currentStep === 2 && role === 5) {
      if (!isPaymentSuccess) {
        toast.error("Please complete payment before proceeding");
        return false; // 🚫 BLOCK NEXT STEP
      }
    }

    if (currentStep === 3) {
      if (!form.panNumber) {
        newErrors.panNumber = "Enter PAN number";
      }

      if (!isPanAlreadyVerified && !panApiResponse && !form.panVerified) {
        newErrors.panNumber = "Please verify PAN before proceeding";
      }
    }

    if (currentStep === 4) {
      if (!form.gstTreatment) newErrors.gstTreatment = "Select GST treatment";

      if (form.gstTreatment !== "unregistered" && !gstVerified) {
        newErrors.gstin = "Please verify GST before proceeding";
      }

      if (form.gstTreatment !== "unregistered" && !form.businessName)
        newErrors.businessName = "Enter legal business name";
    }

    if (currentStep === 5) {
      if (!form.accountNumber) newErrors.accountNumber = "Enter account number";
      if (!form.ifsc) newErrors.ifsc = "Enter IFSC code";
      if (!form.bankName) newErrors.bankName = "Enter bank name";
      // if (!form.bankProofType) newErrors.bankProofType = "Select file type to upload";
      // if (!form.bankProofFile) newErrors.bankProofFile = "Select file to upload";
      if (!form.authorisedName)
        newErrors.authorisedName = "Authorised person required";
      if (!bankVerified) {
          newErrors.accountNumber = "Verify bank account before proceeding";
        }
    }

    if (currentStep === 6) {
      if (!form.agreed) newErrors.agreed = "You must accept the agreement";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ------------------- API POST FUNCTION -------------------
  // const postStepUpdate = async (payload, stepName) => {
  //   try {
  //     setLoading(true);
  //     const res = await fetch(`${BASE_URL}common/update`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(payload),
  //     });

  //     const data = await res.json();

  //     if (!res.ok || !data.status) {
  //       toast.error(data.message || `${stepName} failed`);
  //       return false;
  //     }

  //     toast.success(`${stepName} Saved`);
  //     return true;
  //   } catch {
  //     toast.error("Something went wrong");
  //     return false;
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const postStepUpdate = async (payload, stepName) => {
  try {
    setLoading(true);

    const res = await fetch(`${BASE_URL}common/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (!res.ok || !data.status) {
      toast.error(data.message || `${stepName} failed`);
      return false;
    }

    toast.success(`${stepName} Saved`);

    // 🔥🔥 IMPORTANT: Update session so refresh shows updated step
    if (data?.data) {
      localStorage.setItem("authUserData", JSON.stringify(data.data));
    }

    return true;
  } catch (err) {
    toast.error("Something went wrong");
    return false;
  } finally {
    setLoading(false);
  }
};


  // ------------------- STEP PAYLOAD -------------------
  const stepPayloads = {
    1: {
      contact_email: form.email,
      phone: form.phone,
      address_line1: form.address_line1,
      address_line2: form.address_line2,
      state: state,
      district: district,
      city: city,
      pincode: pincode,
      role: role,
      company_id: company_id,
      is_verified: 1,
      zoho_id: zohoId,
    },
    2: {
      contact_email: form.email,
      payment_ref: form.paymentRef,
      package_id: selectedPackage?.id,
      package_name: selectedPackage?.package_name,
      badge_name: selectedPackage?.badge?.name,
      badge_id: selectedPackage?.badge?.id,
      benefits: selectedPackage?.badge?.benefits,
      eligibility: selectedPackage?.badge?.eligibility,
      lead_amount: selectedPackage?.amount,
      is_payment_success: isPaymentSuccess ? 1 : 0,
      is_verified: 2,
      pincode: pincode,
      role: role,
    },
    3: {
        contact_email: form.email,
        pan: form.panNumber,
        is_verified: 3,
        pan_verified: isPanAlreadyVerified || panApiResponse ? 1 : 0,
        pan_json: panApiResponse || sessionUser?.pan_json || null,
        pincode: pincode,
        role: role,
      },

    4: {
        contact_email: form.email,
        business_type: form.gstTreatment,
        gst: form.gstin || gstResponse?.GSTIN,
        pan_number: isUnregistered ? form.gstin : "",

        // ✅ GST verification flags
        gst_verified: gstVerified ? 1 : 0,
        gst_json: gstResponse ? gstResponse : null,

        business_name: form.businessName,
        trade_name: form.tradeName || gstResponse?.trade_name_of_business,

        is_verified: 4,
        pincode: pincode,
        role: role,
      },

    // 5: {
    //   contact_email: form.email,
    //   account_type: form.accountType,
    //   account_no: form.accountNumber,
    //   ifsc_code: form.ifsc,
    //   bank_name: form.bankName,
    //   branch_name: form.branchname,
    //   authorised_name: form.authorisedName,
    //   is_verified: 5,
    //   pincode: pincode,
    //   role: role,
    // },

    5: {
        contact_email: form.email,
        account_type: form.accountType,
        account_no: form.accountNumber,
        ifsc_code: bankResponse?.ifsc_details?.ifsc || form.ifsc,
        bank_name: bankResponse?.bank_name,
        branch_name: bankResponse?.branch,
        authorised_name: form.authorisedName,

        bank_verified: bankVerified ? 1 : 0,
        bank_json: bankResponse || null,

        is_verified: 5,
        pincode: pincode,
        role: role,
      },

    6: {
      contact_email: form.email,
      is_verified: 7,
      pincode: pincode,
      role: role,
    },
  };

  // const verifyAadhaar = async () => {
  //   if (!form.aadhaarLast4 || form.aadhaarLast4.length !== 4) {
  //     toast.error("Enter valid last 4-digit Aadhaar");
  //     return false;
  //   }

  //   try {
  //     setLoading(true);
  //     const res = await fetch(`${BASE_URL}upload-for-esign/${role}/${user.id}`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ aadhaar_last_four_digit: form.aadhaarLast4 }),
  //     });

  //     const data = await res.json();

  //     if (!data.status) {
  //       toast.error(data.message || "Aadhaar verification failed");
  //       setForm((prev) => ({ ...prev, aadhaarVerified: false }));
  //       return false;
  //     }

  //     toast.success("Aadhaar verified successfully!");
  //     setForm((prev) => ({ ...prev, aadhaarVerified: true }));
  //     return true;
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const verifyAadhaar = async () => {
  if (!form.aadhaarLast4 || form.aadhaarLast4.length !== 4) {
    toast.error("Enter valid last 4 digits");
    return;
  }

  const openCenteredPopup = (url, width = 600, height = 600) => {
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    window.open(
      url,
      "_blank",
      `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
    );
  };

  try {
    setAadhaarStatus("loading");

    // ✅ Get verificationId from session
    const sessionData = localStorage.getItem("authUserData");
    if (!sessionData) return;

    const retailer = JSON.parse(sessionData);
    const esignData = retailer?.esign_json ? JSON.parse(retailer.esign_json) : null;
    const verificationId = esignData?.verification_id;

    if (verificationId) {
      try{
      setEsignStatusLoading(true);
      // 🔹 Check e-Sign status first
      const statusRes = await fetch(
        `${SWAGGER_URL}/Cashfree/get-esign-status?verificationId=${verificationId}`,
        {
          method: "GET",
          headers: {
            "AppId": APPID,
            "SecretKey": SecretKey,
            "Content-Type": "application/json",
          },
        }
      );

      const statusData = await statusRes.json();

      if (statusData.status === "SUCCESS") {
        toast.success("Aadhaar e-Sign already completed!");
        setForm(prev => ({ ...prev, aadhaarVerified: true }));
        setAadhaarStatus("success");
        return;
      }

      if (statusData.status === "IN Progress") {
        const signingLink =
          statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

        if (signingLink) {
          toast.info("e-Sign in progress — opening signing window...");
          openCenteredPopup(signingLink);
          setAadhaarStatus("verifying");
          return;
        }
      }
    } finally {
      setEsignStatusLoading(false); 
    }
  }

    // 🔹 Initiate new eSign if not started
    const res = await fetch(`${BASE_URL}upload-for-esign/${role}/${user.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ aadhaar_last_four_digit: form.aadhaarLast4 }),
    });

    const data = await res.json();

    // 🟠 Already initiated
    if (data.message?.includes("Avoid duplicate requests") && data?.data?.esign_json) {
      const esignData = JSON.parse(data.data.esign_json);
      const signingLink = esignData?.signing_link;
      if (signingLink) {
        toast.success("eSign already initiated — opening window...");
        openCenteredPopup(signingLink);
        setAadhaarStatus("verifying");
        return;
      }
      toast.error("Signing link not found.");
      setAadhaarStatus("error");
      return;
    }

    // 🟢 New eSign
    if (data.status && data.esign_response) {
      const signingLink = data.esign_response.signing_link;
      toast.success("Opening e-Sign window...");
      openCenteredPopup(signingLink);
      setAadhaarStatus("verifying");
      return;
    }

    toast.error(data.message || "Aadhaar verification failed");
    setAadhaarStatus("error");

  } catch (err) {
    console.error(err);
    toast.error("Something went wrong");
    setAadhaarStatus("error");
  }
};

// ✅ Automatic check when step 7 loads and auto-submit if eSign done
  useEffect(() => {
    const checkEsignStatusOnLoad = async () => {
      if (currentStep !== 7) return;

      const sessionData = localStorage.getItem("authUserData");
      if (!sessionData) return;

      let retailer;
      try {
        retailer = JSON.parse(sessionData);
      } catch (err) {
        console.error("Invalid session data", err);
        return;
      }

      const esignData = retailer?.esign_json ? JSON.parse(retailer.esign_json) : null;
      const verificationId = esignData?.verification_id;
      if (!verificationId) return;

      try {
        setAadhaarStatus("loading");

        const statusRes = await fetch(
          `${SWAGGER_URL}/Cashfree/get-esign-status?verificationId=${verificationId}`,
          {
            method: "GET",
            headers: {
              "AppId": APPID,
              "SecretKey": SecretKey,
              "Content-Type": "application/json",
            },
          }
        );

        const statusData = await statusRes.json();

        if (statusData.status === "SUCCESS") {
          toast.success("Aadhaar e-Sign already completed!");
          setForm(prev => ({ ...prev, aadhaarVerified: true }));
          setAadhaarStatus("success");

          // ✅ Auto-call update API for step 7
          const success = await postStepUpdate(stepPayloads[7], "E-Sign Verification");
          if (success) {
            setSubmitted(true); // Move to SuccessSummary
          }
        } else if (statusData.status === "IN Progress") {
          const signingLink =
            statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

          if (signingLink) {
            toast.info("e-Sign in progress — opening signing window...");
            const openCenteredPopup = (url, width = 600, height = 600) => {
              const left = (window.innerWidth - width) / 2;
              const top = (window.innerHeight - height) / 2;
              window.open(
                url,
                "_blank",
                `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
              );
            };
            openCenteredPopup(signingLink);
            setAadhaarStatus("verifying");
          }
        } else {
          setAadhaarStatus("error");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to check e-Sign status");
        setAadhaarStatus("error");
      }
    };

    checkEsignStatusOnLoad();
  }, [currentStep]);


  // ------------------- NEXT BUTTON -------------------
//  const handleNext = async () => {
//   // ✅ Step validation before anything
//   const isValid = validateStep();
//   if (!isValid) {
//     toast.error("Please complete all required fields before proceeding.");
//     return; // Stop moving to next step
//   }

//   // ✅ Step 7: Aadhaar eSign must be complete
//   if (currentStep === 7 && !form.aadhaarVerified) {
//     toast.error("Complete Aadhaar e-Sign before proceeding.");
//     return;
//   }

//   // --- KEEP ALL YOUR EXISTING STEP 6 AND NEXT LOGIC BELOW ---
//   if (currentStep === 6) {
//     const sessionData = localStorage.getItem("authUserData");
//     if (sessionData) {
//       const retailer = JSON.parse(sessionData);
//       const esignData = retailer.esign_json ? JSON.parse(retailer.esign_json) : null;
//       const verificationId = esignData?.verification_id;
//       console.log("verification id: ", verificationId);

//       if (verificationId) {
//         try {
//           setAadhaarStatus("loading");

//           const res = await fetch(
//             `https://agitated-cori.103-195-185-254.plesk.page/api/Cashfree/get-esign-status?verificationId=${verificationId}`,
//             {
//               method: "GET",
//               headers: {
//                 "AppId": APPID,
//                 "SecretKey": SecretKey,
//                 "Content-Type": "application/json",
//               },
//             }
//           );

//           const statusData = await res.json();

//           if (statusData.status === "SUCCESS") {
//             toast.success("Aadhaar e-Sign already completed!");
//             setForm(prev => ({ ...prev, aadhaarVerified: true }));
//             setAadhaarStatus("success");
//           } else if (statusData.status === "IN Progress") {
//             const signingLink =
//               statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

//             if (signingLink) {
//               toast.info("e-Sign in progress — opening popup...");
//               const openCenteredPopup = (url, width = 600, height = 600) => {
//                 const left = (window.innerWidth - width) / 2;
//                 const top = (window.innerHeight - height) / 2;
//                 window.open(
//                   url,
//                   "_blank",
//                   `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
//                 );
//               };
//               openCenteredPopup(signingLink);
//               setAadhaarStatus("verifying");
//             }
//           }
//         } catch (err) {
//           console.error(err);
//           toast.error("Failed to check e-Sign status");
//           setAadhaarStatus("error");
//         }
//       }
//     }
//   }

//   // Move to next step only if all checks pass
//   setCurrentStep(prev => prev + 1);
// };

//   const handleNext = async () => {
//   const isValid = validateStep();
//   if (!isValid) {
//     toast.error("Please complete all required fields before proceeding.");
//     return;
//   }

//   const success = await postStepUpdate(
//     stepPayloads[currentStep],
//     `Step ${currentStep}`
//   );

//   if (!success) return;

//   // 🔥 If step 6 → finish onboarding
//   if (currentStep === 6) {
//     setSubmitted(true); // show SuccessSummary
//     return;
//   }

//   setCurrentStep(prev => prev + 1);
// };

const handleNext = async () => {
  const isValid = validateStep();
  if (!isValid) {
    toast.error("Please complete all required fields before proceeding.");
    return;
  }

  const success = await postStepUpdate(
    stepPayloads[currentStep],
    `Step ${currentStep}`
  );

  if (!success) return;

  // 🔥 FINISH ON STEP 6
  if (currentStep === 6) {
    setSubmitted(true);
    return;
  }

  // 🔥 AUTO-SKIP PAYMENT FOR ROLE 2 & 4
  if ((role === 2 || role === 4) && currentStep === 1) {
    setCurrentStep(3); // jump directly to PAN
    return;
  }

  setCurrentStep(prev => prev + 1);
};




  // ------------------- BACK BUTTON -------------------
  // const handleBack = () => {
  //   if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  // };

  const handleBack = () => {
    if ((role === 2 || role === 4) && currentStep === 3) {
      setCurrentStep(1);
      return;
    }
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };


  return (
    <div className="w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
      <Header />

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar Stepper */}
        <aside className="w-full md:w-60">
          <Stepper steps={displaySteps} currentStep={currentStep} />
        </aside>

        {/* Main Content */}
        <main className="flex-1">
          {submitted ? (
            <SuccessSummary form={form} />
          ) : (
            <>
              <FormBody
                currentStep={currentStep}
                getDisplayStepNumber={getDisplayStepNumber}
                form={form}
                errors={errors}
                updateField={updateField}
                selectedPackage={selectedPackage}
                setSelectedPackage={setSelectedPackage}
                panApiResponse={panApiResponse}
                setPanApiResponse={setPanApiResponse}
                verifyAadhaar={verifyAadhaar} // ✅ Pass function
                aadhaarStatus={aadhaarStatus}
                setAadhaarStatus={setAadhaarStatus}
                setForm={setForm}
                esignStatusLoading={esignStatusLoading}
                emailVerified={emailVerified}
                setEmailVerified={setEmailVerified}
                phoneVerified={phoneVerified}
                setPhoneVerified={setPhoneVerified}
                setEsignStatusLoading={setEsignStatusLoading}
                setIsPaymentSuccess={setIsPaymentSuccess}
                gstVerified={gstVerified}
                setGstVerified={setGstVerified}
                gstResponse={gstResponse}
                setGstResponse={setGstResponse}
                bankVerified={bankVerified}
                setBankVerified={setBankVerified}
                bankResponse={bankResponse}
                setBankResponse={setBankResponse}
                setZohoId={setZohoId}
                zohoId={zohoId}

              />
              <Footer
                currentStep={currentStep}
                onBack={handleBack}
                onNext={handleNext}
                isLast={currentStep === steps.length}
                loading={loading}
                bankVerified={bankVerified}
              />
            </>
          )}
        </main>
      </div>
    </div>

  );
}



// import { useEffect, useState } from "react";
// import Header from "../../../../Component/RetailerComponents/Header";
// import Stepper from "../../../../Component/RetailerComponents/Stepper";
// import SuccessSummary from "../../../../Component/RetailerComponents/SuccessSummary";
// import FormBody from "../../../../Component/RetailerComponents/FormBody";
// import Footer from "../../../../Component/RetailerComponents/Footer";
// import toast from "react-hot-toast";
// import { APPID, BASE_URL, SecretKey, SWAGGER_URL } from "../../../../config/api";

// // Step Labels
// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "Payment" },
//   { id: 3, name: "PAN Verification" },
//   { id: 4, name: "GST Details" },
//   { id: 5, name: "Bank Details" },
//   { id: 6, name: "Agreement" },
// ];

// export default function RetailerOnboarding() {
//   const user = JSON.parse(localStorage.getItem("authUserData") || "{}");

//   const isVerified = Number(user?.is_verified || 0);

//   // Find next backend step
//   const nextStep =
//     steps.find(step => step.id > isVerified) ||
//     steps[steps.length - 1];

//   const initialStep = nextStep.id;


//   const [currentStep, setCurrentStep] = useState(initialStep);
//   const [submitted, setSubmitted] = useState(isVerified >= steps.length);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [selectedPackage, setSelectedPackage] = useState(null);
//   const [panApiResponse, setPanApiResponse] = useState(null);
//   const [aadhaarStatus, setAadhaarStatus] = useState("idle");
//   const [esignStatusLoading, setEsignStatusLoading] = useState(false);
//   const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);

//   const [gstVerified, setGstVerified] = useState(false);
//   const [gstResponse, setGstResponse] = useState(null);

//   const [bankVerified, setBankVerified] = useState(false);
//   const [bankResponse, setBankResponse] = useState(null);
//   const [zohoId, setZohoId] = useState(
//   user?.zoho_id || null   // restore if already present
// );
// console.log("set zoho id: ",setZohoId)




//   const [form, setForm] = useState({
//     email: user?.contact_email || "",
//     phone: user?.phone || "",
//     paymentRef: "",
//     panNumber: "",
//     panVerified: false,
//     gstTreatment: "",
//     gstin: "",
//     businessName: "",
//     tradeName: "",
//     accountNumber: "",
//     ifsc: "",
//     bankName: "",
//     authorisedName: "",
//     agreed: false,
//     aadhaar: "",          // ✅ Add Aadhaar field
//     aadhaarVerified: false, // ✅ Track verification status
//   });


//   const address_line_1 = user?.address_line1;
//   const address_line_2 = user?.address_line2;
//   const state = user?.state;
//   const district = user?.district;
//   const city = user?.city;
//   const pincode = user?.pincode;
//   const role = user?.role;
//   const company_id = user?.company_id;

//   const sessionUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

//   const isPanVerified =
//   form.panVerified === true ||
//   panApiResponse !== null ||
//   Number(sessionUser?.pan_verified) === 1;

//   const isPanAlreadyVerified =
//   Number(sessionUser?.pan_verified) === 1 || form.panVerified === true;



// // UI-visible steps (renumbered only for display)
// const displaySteps =
//   role === 5
//     ? steps.map((step, index) => ({
//         ...step,
//         displayIndex: index + 1,
//       }))
//     : steps
//         .filter(step => step.id !== 2) // 🚫 hide payment
//         .map((step, index) => ({
//           ...step,
//           displayIndex: index + 1, // 🔥 UI renumbering
//         }));


//   const getDisplayStepNumber = (stepId) => {
//   if (role === 5) return stepId; // retailer → normal

//   // role 2 & 4: payment skipped
//   if (stepId > 2) return stepId - 1;

//   return stepId;
// };
 

//   const isUnregistered = form.gstTreatment === "unregistered";

//   const updateField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   // Simple PAN validation (5 letters, 4 digits, 1 letter)
//   const isValidPAN = (pan) => {
//     const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
//     return panRegex.test(pan.toUpperCase());
//   };


//   // ------------------- VALIDATION -------------------
//   const validateStep = () => {
//     const newErrors = {};

//     if (currentStep === 1) {
//       if (!form.email) newErrors.email = "Enter email";
//       if (!form.phone) newErrors.phone = "Enter phone number";
//     }

//     if (currentStep === 2 && role === 5) {
//       if (!isPaymentSuccess) {
//         toast.error("Please complete payment before proceeding");
//         return false; // 🚫 BLOCK NEXT STEP
//       }
//     }

//     if (currentStep === 3) {
//       if (!form.panNumber) {
//         newErrors.panNumber = "Enter PAN number";
//       }

//       if (!isPanAlreadyVerified && !panApiResponse && !form.panVerified) {
//         newErrors.panNumber = "Please verify PAN before proceeding";
//       }
//     }

//     if (currentStep === 4) {
//       if (!form.gstTreatment) newErrors.gstTreatment = "Select GST treatment";

//       if (form.gstTreatment !== "unregistered" && !gstVerified) {
//         newErrors.gstin = "Please verify GST before proceeding";
//       }

//       if (form.gstTreatment !== "unregistered" && !form.businessName)
//         newErrors.businessName = "Enter legal business name";
//     }

//     if (currentStep === 5) {
//       if (!form.accountNumber) newErrors.accountNumber = "Enter account number";
//       if (!form.ifsc) newErrors.ifsc = "Enter IFSC code";
//       if (!form.bankName) newErrors.bankName = "Enter bank name";
//       // if (!form.bankProofType) newErrors.bankProofType = "Select file type to upload";
//       // if (!form.bankProofFile) newErrors.bankProofFile = "Select file to upload";
//       if (!form.authorisedName)
//         newErrors.authorisedName = "Authorised person required";
//       if (!bankVerified) {
//           newErrors.accountNumber = "Verify bank account before proceeding";
//         }
//     }

//     if (currentStep === 6) {
//       if (!form.agreed) newErrors.agreed = "You must accept the agreement";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // ------------------- API POST FUNCTION -------------------
//   // const postStepUpdate = async (payload, stepName) => {
//   //   try {
//   //     setLoading(true);
//   //     const res = await fetch(`${BASE_URL}common/update`, {
//   //       method: "POST",
//   //       headers: { "Content-Type": "application/json" },
//   //       body: JSON.stringify(payload),
//   //     });

//   //     const data = await res.json();

//   //     if (!res.ok || !data.status) {
//   //       toast.error(data.message || `${stepName} failed`);
//   //       return false;
//   //     }

//   //     toast.success(`${stepName} Saved`);
//   //     return true;
//   //   } catch {
//   //     toast.error("Something went wrong");
//   //     return false;
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const postStepUpdate = async (payload, stepName) => {
//   try {
//     setLoading(true);

//     const res = await fetch(`${BASE_URL}common/update`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     });

//     const data = await res.json();

//     if (!res.ok || !data.status) {
//       toast.error(data.message || `${stepName} failed`);
//       return false;
//     }

//     toast.success(`${stepName} Saved`);

//     // 🔥🔥 IMPORTANT: Update session so refresh shows updated step
//     if (data?.data) {
//       localStorage.setItem("authUserData", JSON.stringify(data.data));
//     }

//     return true;
//   } catch (err) {
//     toast.error("Something went wrong");
//     return false;
//   } finally {
//     setLoading(false);
//   }
// };


//   // ------------------- STEP PAYLOAD -------------------
//   const stepPayloads = {
//     1: {
//       contact_email: form.email,
//       phone: form.phone,
//       address_line1: address_line_1,
//       address_line2: address_line_2,
//       state: state,
//       district: district,
//       city: city,
//       pincode: pincode,
//       role: role,
//       company_id: company_id,
//       is_verified: 1,
//       zoho_id: zohoId,
//     },
//     2: {
//       contact_email: form.email,
//       payment_ref: form.paymentRef,
//       package_id: selectedPackage?.id,
//       package_name: selectedPackage?.package_name,
//       badge_name: selectedPackage?.badge?.name,
//       badge_id: selectedPackage?.badge?.id,
//       benefits: selectedPackage?.badge?.benefits,
//       eligibility: selectedPackage?.badge?.eligibility,
//       lead_amount: selectedPackage?.amount,
//       is_payment_success: isPaymentSuccess ? 1 : 0,
//       is_verified: 2,
//       pincode: pincode,
//       role: role,
//     },
//     3: {
//         contact_email: form.email,
//         pan: form.panNumber,
//         is_verified: 3,
//         pan_verified: isPanAlreadyVerified || panApiResponse ? 1 : 0,
//         pan_json: panApiResponse || sessionUser?.pan_json || null,
//         pincode: pincode,
//         role: role,
//       },

//     4: {
//         contact_email: form.email,
//         business_type: form.gstTreatment,
//         gst: form.gstin,
//         pan_number: isUnregistered ? form.gstin : "",

//         // ✅ GST verification flags
//         gst_verified: gstVerified ? 1 : 0,
//         gst_json: gstResponse ? gstResponse : null,

//         business_name: form.businessName,
//         trade_name: form.tradeName,

//         is_verified: 4,
//         pincode: pincode,
//         role: role,
//       },

//     // 5: {
//     //   contact_email: form.email,
//     //   account_type: form.accountType,
//     //   account_no: form.accountNumber,
//     //   ifsc_code: form.ifsc,
//     //   bank_name: form.bankName,
//     //   branch_name: form.branchname,
//     //   authorised_name: form.authorisedName,
//     //   is_verified: 5,
//     //   pincode: pincode,
//     //   role: role,
//     // },

//     5: {
//         contact_email: form.email,
//         account_type: form.accountType,
//         account_no: form.accountNumber,
//         ifsc_code: bankResponse?.ifsc_details?.ifsc || form.ifsc,
//         bank_name: bankResponse?.bank_name,
//         branch_name: bankResponse?.branch,
//         authorised_name: form.authorisedName,

//         bank_verified: bankVerified ? 1 : 0,
//         bank_json: bankResponse || null,

//         is_verified: 5,
//         pincode: pincode,
//         role: role,
//       },

//     6: {
//       contact_email: form.email,
//       is_verified: 7,
//       pincode: pincode,
//       role: role,
//     },
//   };

//   // const verifyAadhaar = async () => {
//   //   if (!form.aadhaarLast4 || form.aadhaarLast4.length !== 4) {
//   //     toast.error("Enter valid last 4-digit Aadhaar");
//   //     return false;
//   //   }

//   //   try {
//   //     setLoading(true);
//   //     const res = await fetch(`${BASE_URL}upload-for-esign/${role}/${user.id}`, {
//   //       method: "POST",
//   //       headers: { "Content-Type": "application/json" },
//   //       body: JSON.stringify({ aadhaar_last_four_digit: form.aadhaarLast4 }),
//   //     });

//   //     const data = await res.json();

//   //     if (!data.status) {
//   //       toast.error(data.message || "Aadhaar verification failed");
//   //       setForm((prev) => ({ ...prev, aadhaarVerified: false }));
//   //       return false;
//   //     }

//   //     toast.success("Aadhaar verified successfully!");
//   //     setForm((prev) => ({ ...prev, aadhaarVerified: true }));
//   //     return true;
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const verifyAadhaar = async () => {
//   if (!form.aadhaarLast4 || form.aadhaarLast4.length !== 4) {
//     toast.error("Enter valid last 4 digits");
//     return;
//   }

//   const openCenteredPopup = (url, width = 600, height = 600) => {
//     const left = (window.innerWidth - width) / 2;
//     const top = (window.innerHeight - height) / 2;
//     window.open(
//       url,
//       "_blank",
//       `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
//     );
//   };

//   try {
//     setAadhaarStatus("loading");

//     // ✅ Get verificationId from session
//     const sessionData = localStorage.getItem("authUserData");
//     if (!sessionData) return;

//     const retailer = JSON.parse(sessionData);
//     const esignData = retailer?.esign_json ? JSON.parse(retailer.esign_json) : null;
//     const verificationId = esignData?.verification_id;

//     if (verificationId) {
//       try{
//       setEsignStatusLoading(true);
//       // 🔹 Check e-Sign status first
//       const statusRes = await fetch(
//         `${SWAGGER_URL}/Cashfree/get-esign-status?verificationId=${verificationId}`,
//         {
//           method: "GET",
//           headers: {
//             "AppId": APPID,
//             "SecretKey": SecretKey,
//             "Content-Type": "application/json",
//           },
//         }
//       );

//       const statusData = await statusRes.json();

//       if (statusData.status === "SUCCESS") {
//         toast.success("Aadhaar e-Sign already completed!");
//         setForm(prev => ({ ...prev, aadhaarVerified: true }));
//         setAadhaarStatus("success");
//         return;
//       }

//       if (statusData.status === "IN Progress") {
//         const signingLink =
//           statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

//         if (signingLink) {
//           toast.info("e-Sign in progress — opening signing window...");
//           openCenteredPopup(signingLink);
//           setAadhaarStatus("verifying");
//           return;
//         }
//       }
//     } finally {
//       setEsignStatusLoading(false); 
//     }
//   }

//     // 🔹 Initiate new eSign if not started
//     const res = await fetch(`${BASE_URL}upload-for-esign/${role}/${user.id}`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ aadhaar_last_four_digit: form.aadhaarLast4 }),
//     });

//     const data = await res.json();

//     // 🟠 Already initiated
//     if (data.message?.includes("Avoid duplicate requests") && data?.data?.esign_json) {
//       const esignData = JSON.parse(data.data.esign_json);
//       const signingLink = esignData?.signing_link;
//       if (signingLink) {
//         toast.success("eSign already initiated — opening window...");
//         openCenteredPopup(signingLink);
//         setAadhaarStatus("verifying");
//         return;
//       }
//       toast.error("Signing link not found.");
//       setAadhaarStatus("error");
//       return;
//     }

//     // 🟢 New eSign
//     if (data.status && data.esign_response) {
//       const signingLink = data.esign_response.signing_link;
//       toast.success("Opening e-Sign window...");
//       openCenteredPopup(signingLink);
//       setAadhaarStatus("verifying");
//       return;
//     }

//     toast.error(data.message || "Aadhaar verification failed");
//     setAadhaarStatus("error");

//   } catch (err) {
//     console.error(err);
//     toast.error("Something went wrong");
//     setAadhaarStatus("error");
//   }
// };

// // ✅ Automatic check when step 7 loads and auto-submit if eSign done
//   useEffect(() => {
//     const checkEsignStatusOnLoad = async () => {
//       if (currentStep !== 7) return;

//       const sessionData = localStorage.getItem("authUserData");
//       if (!sessionData) return;

//       let retailer;
//       try {
//         retailer = JSON.parse(sessionData);
//       } catch (err) {
//         console.error("Invalid session data", err);
//         return;
//       }

//       const esignData = retailer?.esign_json ? JSON.parse(retailer.esign_json) : null;
//       const verificationId = esignData?.verification_id;
//       if (!verificationId) return;

//       try {
//         setAadhaarStatus("loading");

//         const statusRes = await fetch(
//           `${SWAGGER_URL}/Cashfree/get-esign-status?verificationId=${verificationId}`,
//           {
//             method: "GET",
//             headers: {
//               "AppId": APPID,
//               "SecretKey": SecretKey,
//               "Content-Type": "application/json",
//             },
//           }
//         );

//         const statusData = await statusRes.json();

//         if (statusData.status === "SUCCESS") {
//           toast.success("Aadhaar e-Sign already completed!");
//           setForm(prev => ({ ...prev, aadhaarVerified: true }));
//           setAadhaarStatus("success");

//           // ✅ Auto-call update API for step 7
//           const success = await postStepUpdate(stepPayloads[7], "E-Sign Verification");
//           if (success) {
//             setSubmitted(true); // Move to SuccessSummary
//           }
//         } else if (statusData.status === "IN Progress") {
//           const signingLink =
//             statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

//           if (signingLink) {
//             toast.info("e-Sign in progress — opening signing window...");
//             const openCenteredPopup = (url, width = 600, height = 600) => {
//               const left = (window.innerWidth - width) / 2;
//               const top = (window.innerHeight - height) / 2;
//               window.open(
//                 url,
//                 "_blank",
//                 `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
//               );
//             };
//             openCenteredPopup(signingLink);
//             setAadhaarStatus("verifying");
//           }
//         } else {
//           setAadhaarStatus("error");
//         }
//       } catch (err) {
//         console.error(err);
//         toast.error("Failed to check e-Sign status");
//         setAadhaarStatus("error");
//       }
//     };

//     checkEsignStatusOnLoad();
//   }, [currentStep]);


//   // ------------------- NEXT BUTTON -------------------
// //  const handleNext = async () => {
// //   // ✅ Step validation before anything
// //   const isValid = validateStep();
// //   if (!isValid) {
// //     toast.error("Please complete all required fields before proceeding.");
// //     return; // Stop moving to next step
// //   }

// //   // ✅ Step 7: Aadhaar eSign must be complete
// //   if (currentStep === 7 && !form.aadhaarVerified) {
// //     toast.error("Complete Aadhaar e-Sign before proceeding.");
// //     return;
// //   }

// //   // --- KEEP ALL YOUR EXISTING STEP 6 AND NEXT LOGIC BELOW ---
// //   if (currentStep === 6) {
// //     const sessionData = localStorage.getItem("authUserData");
// //     if (sessionData) {
// //       const retailer = JSON.parse(sessionData);
// //       const esignData = retailer.esign_json ? JSON.parse(retailer.esign_json) : null;
// //       const verificationId = esignData?.verification_id;
// //       console.log("verification id: ", verificationId);

// //       if (verificationId) {
// //         try {
// //           setAadhaarStatus("loading");

// //           const res = await fetch(
// //             `https://agitated-cori.103-195-185-254.plesk.page/api/Cashfree/get-esign-status?verificationId=${verificationId}`,
// //             {
// //               method: "GET",
// //               headers: {
// //                 "AppId": APPID,
// //                 "SecretKey": SecretKey,
// //                 "Content-Type": "application/json",
// //               },
// //             }
// //           );

// //           const statusData = await res.json();

// //           if (statusData.status === "SUCCESS") {
// //             toast.success("Aadhaar e-Sign already completed!");
// //             setForm(prev => ({ ...prev, aadhaarVerified: true }));
// //             setAadhaarStatus("success");
// //           } else if (statusData.status === "IN Progress") {
// //             const signingLink =
// //               statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

// //             if (signingLink) {
// //               toast.info("e-Sign in progress — opening popup...");
// //               const openCenteredPopup = (url, width = 600, height = 600) => {
// //                 const left = (window.innerWidth - width) / 2;
// //                 const top = (window.innerHeight - height) / 2;
// //                 window.open(
// //                   url,
// //                   "_blank",
// //                   `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
// //                 );
// //               };
// //               openCenteredPopup(signingLink);
// //               setAadhaarStatus("verifying");
// //             }
// //           }
// //         } catch (err) {
// //           console.error(err);
// //           toast.error("Failed to check e-Sign status");
// //           setAadhaarStatus("error");
// //         }
// //       }
// //     }
// //   }

// //   // Move to next step only if all checks pass
// //   setCurrentStep(prev => prev + 1);
// // };

// //   const handleNext = async () => {
// //   const isValid = validateStep();
// //   if (!isValid) {
// //     toast.error("Please complete all required fields before proceeding.");
// //     return;
// //   }

// //   const success = await postStepUpdate(
// //     stepPayloads[currentStep],
// //     `Step ${currentStep}`
// //   );

// //   if (!success) return;

// //   // 🔥 If step 6 → finish onboarding
// //   if (currentStep === 6) {
// //     setSubmitted(true); // show SuccessSummary
// //     return;
// //   }

// //   setCurrentStep(prev => prev + 1);
// // };

// const handleNext = async () => {
//   const isValid = validateStep();
//   if (!isValid) {
//     toast.error("Please complete all required fields before proceeding.");
//     return;
//   }

//   const success = await postStepUpdate(
//     stepPayloads[currentStep],
//     `Step ${currentStep}`
//   );

//   if (!success) return;

//   // 🔥 FINISH ON STEP 6
//   if (currentStep === 6) {
//     setSubmitted(true);
//     return;
//   }

//   // 🔥 AUTO-SKIP PAYMENT FOR ROLE 2 & 4
//   if ((role === 2 || role === 4) && currentStep === 1) {
//     setCurrentStep(3); // jump directly to PAN
//     return;
//   }

//   setCurrentStep(prev => prev + 1);
// };




//   // ------------------- BACK BUTTON -------------------
//   // const handleBack = () => {
//   //   if (currentStep > 1) setCurrentStep((prev) => prev - 1);
//   // };

//   const handleBack = () => {
//     if ((role === 2 || role === 4) && currentStep === 3) {
//       setCurrentStep(1);
//       return;
//     }
//     if (currentStep > 1) setCurrentStep(prev => prev - 1);
//   };


//   return (
//     <div className="w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
//       <Header />

//       <div className="flex flex-col md:flex-row gap-6">
//         {/* Sidebar Stepper */}
//         <aside className="w-full md:w-60">
//           <Stepper steps={displaySteps} currentStep={currentStep} />
//         </aside>

//         {/* Main Content */}
//         <main className="flex-1">
//           {submitted ? (
//             <SuccessSummary form={form} />
//           ) : (
//             <>
//               <FormBody
//                 currentStep={currentStep}
//                 getDisplayStepNumber={getDisplayStepNumber}
//                 form={form}
//                 errors={errors}
//                 updateField={updateField}
//                 selectedPackage={selectedPackage}
//                 setSelectedPackage={setSelectedPackage}
//                 panApiResponse={panApiResponse}
//                 setPanApiResponse={setPanApiResponse}
//                 verifyAadhaar={verifyAadhaar} // ✅ Pass function
//                 aadhaarStatus={aadhaarStatus}
//                 setAadhaarStatus={setAadhaarStatus}
//                 setForm={setForm}
//                 esignStatusLoading={esignStatusLoading}
//                 setEsignStatusLoading={setEsignStatusLoading}
//                 setIsPaymentSuccess={setIsPaymentSuccess}
//                 gstVerified={gstVerified}
//                 setGstVerified={setGstVerified}
//                 gstResponse={gstResponse}
//                 setGstResponse={setGstResponse}
//                 bankVerified={bankVerified}
//                 setBankVerified={setBankVerified}
//                 bankResponse={bankResponse}
//                 setBankResponse={setBankResponse}
//                 setZohoId={setZohoId}

//               />
//               <Footer
//                 currentStep={currentStep}
//                 onBack={handleBack}
//                 onNext={handleNext}
//                 isLast={currentStep === steps.length}
//                 loading={loading}
//                 bankVerified={bankVerified}
//               />
//             </>
//           )}
//         </main>
//       </div>
//     </div>

//   );
// }


// ****************************************Full Working Code without payment***********************************************************


// import { useEffect, useState } from "react";
// import Header from "../../../../Component/RetailerComponents/Header";
// import Stepper from "../../../../Component/RetailerComponents/Stepper";
// import SuccessSummary from "../../../../Component/RetailerComponents/SuccessSummary";
// import FormBody from "../../../../Component/RetailerComponents/FormBody";
// import Footer from "../../../../Component/RetailerComponents/Footer";
// import toast from "react-hot-toast";
// import { APPID, BASE_URL, SecretKey, SWAGGER_URL } from "../../../../config/api";

// // Step Labels
// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "Payment" },
//   { id: 3, name: "PAN Verification" },
//   { id: 4, name: "GST Details" },
//   { id: 5, name: "Bank Details" },
//   { id: 6, name: " Agreement" },
//   { id: 7, name: "E-Sign Verify" }, // ✅ New last step
// ];


// export default function RetailerOnboarding() {
//   const user = JSON.parse(localStorage.getItem("authUserData") || "{}");

//   const isVerified = Number(user?.is_verified || 0);

//   // Find next backend step
//   const nextStep =
//     steps.find(step => step.id > isVerified) ||
//     steps[steps.length - 1];

//   const initialStep = nextStep.id;


//   const [currentStep, setCurrentStep] = useState(initialStep);
//   const [submitted, setSubmitted] = useState(isVerified >= steps.length);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [selectedPackage, setSelectedPackage] = useState(null);
//   const [panApiResponse, setPanApiResponse] = useState(null);
//   const [aadhaarStatus, setAadhaarStatus] = useState("idle");
//   const [esignStatusLoading, setEsignStatusLoading] = useState(false);
//   const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);

//   const [gstVerified, setGstVerified] = useState(false);
//   const [gstResponse, setGstResponse] = useState(null);

//   const [bankVerified, setBankVerified] = useState(false);
//   const [bankResponse, setBankResponse] = useState(null);



//   const [form, setForm] = useState({
//     email: user?.contact_email || "",
//     phone: user?.phone || "",
//     paymentRef: "",
//     panNumber: "",
//     panVerified: false,
//     gstTreatment: "",
//     gstin: "",
//     businessName: "",
//     tradeName: "",
//     accountNumber: "",
//     ifsc: "",
//     bankName: "",
//     authorisedName: "",
//     agreed: false,
//     aadhaar: "",          // ✅ Add Aadhaar field
//     aadhaarVerified: false, // ✅ Track verification status
//   });


//   const address_line_1 = user?.address_line1;
//   const address_line_2 = user?.address_line2;
//   const state = user?.state;
//   const district = user?.district;
//   const city = user?.city;
//   const pincode = user?.pincode;
//   const role = user?.role;
//   const company_id = user?.company_id;

//   const sessionUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

//   const isPanVerified =
//   form.panVerified === true ||
//   panApiResponse !== null ||
//   Number(sessionUser?.pan_verified) === 1;

//   const isPanAlreadyVerified =
//   Number(sessionUser?.pan_verified) === 1 || form.panVerified === true;



// // UI-visible steps (renumbered only for display)
// const displaySteps =
//   role === 5
//     ? steps.map((step, index) => ({
//         ...step,
//         displayIndex: index + 1,
//       }))
//     : steps
//         .filter(step => step.id !== 2) // 🚫 hide payment
//         .map((step, index) => ({
//           ...step,
//           displayIndex: index + 1, // 🔥 UI renumbering
//         }));


//   const getDisplayStepNumber = (stepId) => {
//   if (role === 5) return stepId; // retailer → normal

//   // role 2 & 4: payment skipped
//   if (stepId > 2) return stepId - 1;

//   return stepId;
// };
 

//   const isUnregistered = form.gstTreatment === "unregistered";

//   const updateField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   // Simple PAN validation (5 letters, 4 digits, 1 letter)
//   const isValidPAN = (pan) => {
//     const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
//     return panRegex.test(pan.toUpperCase());
//   };


//   // ------------------- VALIDATION -------------------
//   const validateStep = () => {
//     const newErrors = {};

//     if (currentStep === 1) {
//       if (!form.email) newErrors.email = "Enter email";
//       if (!form.phone) newErrors.phone = "Enter phone number";
//     }

//     if (currentStep === 3) {
//       if (!form.panNumber) {
//         newErrors.panNumber = "Enter PAN number";
//       }

//       if (!isPanAlreadyVerified && !panApiResponse && !form.panVerified) {
//         newErrors.panNumber = "Please verify PAN before proceeding";
//       }
//     }

//     if (currentStep === 4) {
//       if (!form.gstTreatment) newErrors.gstTreatment = "Select GST treatment";

//       if (form.gstTreatment !== "unregistered" && !gstVerified) {
//         newErrors.gstin = "Please verify GST before proceeding";
//       }

//       if (!form.businessName)
//         newErrors.businessName = "Enter legal business name";
//     }

//     if (currentStep === 5) {
//       if (!form.accountNumber) newErrors.accountNumber = "Enter account number";
//       if (!form.ifsc) newErrors.ifsc = "Enter IFSC code";
//       if (!form.bankName) newErrors.bankName = "Enter bank name";
//       if (!form.bankProofType) newErrors.bankProofType = "Select file type to upload";
//       if (!form.bankProofFile) newErrors.bankProofFile = "Select file to upload";
//       if (!form.authorisedName)
//         newErrors.authorisedName = "Authorised person required";
//       if (!bankVerified) {
//           newErrors.accountNumber = "Verify bank account before proceeding";
//         }
//     }

//     if (currentStep === 6) {
//       if (!form.agreed) newErrors.agreed = "You must accept the agreement";
//     }


//     if (currentStep === 7) {
//       if (!form.aadhaarLast4) {
//         newErrors.aadhaarLast4 = "Enter last 4 digits of Aadhaar";
//       } else if (!/^\d{4}$/.test(form.aadhaarLast4)) {
//         newErrors.aadhaarLast4 = "Aadhaar must be 4 digits";
//       }
//     }


//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // ------------------- API POST FUNCTION -------------------
//   // const postStepUpdate = async (payload, stepName) => {
//   //   try {
//   //     setLoading(true);
//   //     const res = await fetch(`${BASE_URL}common/update`, {
//   //       method: "POST",
//   //       headers: { "Content-Type": "application/json" },
//   //       body: JSON.stringify(payload),
//   //     });

//   //     const data = await res.json();

//   //     if (!res.ok || !data.status) {
//   //       toast.error(data.message || `${stepName} failed`);
//   //       return false;
//   //     }

//   //     toast.success(`${stepName} Saved`);
//   //     return true;
//   //   } catch {
//   //     toast.error("Something went wrong");
//   //     return false;
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const postStepUpdate = async (payload, stepName) => {
//   try {
//     setLoading(true);

//     const res = await fetch(`${BASE_URL}common/update`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     });

//     const data = await res.json();

//     if (!res.ok || !data.status) {
//       toast.error(data.message || `${stepName} failed`);
//       return false;
//     }

//     toast.success(`${stepName} Saved`);

//     // 🔥🔥 IMPORTANT: Update session so refresh shows updated step
//     if (data?.data) {
//       localStorage.setItem("authUserData", JSON.stringify(data.data));
//     }

//     return true;
//   } catch (err) {
//     toast.error("Something went wrong");
//     return false;
//   } finally {
//     setLoading(false);
//   }
// };


//   // ------------------- STEP PAYLOAD -------------------
//   const stepPayloads = {
//     1: {
//       contact_email: form.email,
//       phone: form.phone,
//       address_line1: address_line_1,
//       address_line2: address_line_2,
//       state: state,
//       district: district,
//       city: city,
//       pincode: pincode,
//       role: role,
//       company_id: company_id,
//       is_verified: 1,
//     },
//     2: {
//       contact_email: form.email,
//       payment_ref: form.paymentRef,
//       package_id: selectedPackage?.id,
//       package_name: selectedPackage?.package_name,
//       badge_name: selectedPackage?.badge?.name,
//       badge_id: selectedPackage?.badge?.id,
//       benefits: selectedPackage?.badge?.benefits,
//       eligibility: selectedPackage?.badge?.eligibility,
//       lead_amount: selectedPackage?.amount,
//       is_payment_success: isPaymentSuccess ? 1 : 0,
//       is_verified: 2,
//       pincode: pincode,
//       role: role,
//     },
//     3: {
//         contact_email: form.email,
//         pan: form.panNumber,
//         is_verified: 3,
//         pan_verified: isPanAlreadyVerified || panApiResponse ? 1 : 0,
//         pan_json: panApiResponse || sessionUser?.pan_json || null,
//         pincode: pincode,
//         role: role,
//       },

//     4: {
//         contact_email: form.email,
//         business_type: form.gstTreatment,
//         gst: form.gstin,
//         pan_number: isUnregistered ? form.gstin : "",

//         // ✅ GST verification flags
//         gst_verified: gstVerified ? 1 : 0,
//         gst_json: gstResponse ? gstResponse : null,

//         business_name: form.businessName,
//         trade_name: form.tradeName,

//         is_verified: 4,
//         pincode: pincode,
//         role: role,
//       },

//     // 5: {
//     //   contact_email: form.email,
//     //   account_type: form.accountType,
//     //   account_no: form.accountNumber,
//     //   ifsc_code: form.ifsc,
//     //   bank_name: form.bankName,
//     //   branch_name: form.branchname,
//     //   authorised_name: form.authorisedName,
//     //   is_verified: 5,
//     //   pincode: pincode,
//     //   role: role,
//     // },

//     5: {
//         contact_email: form.email,
//         account_type: form.accountType,
//         account_no: form.accountNumber,
//         ifsc_code: bankResponse?.ifsc_details?.ifsc || form.ifsc,
//         bank_name: bankResponse?.bank_name,
//         branch_name: bankResponse?.branch,
//         authorised_name: form.authorisedName,

//         bank_verified: bankVerified ? 1 : 0,
//         bank_json: bankResponse || null,

//         is_verified: 5,
//         pincode: pincode,
//         role: role,
//       },

//     6: {
//       contact_email: form.email,
//       is_verified: 6,
//       pincode: pincode,
//       role: role,
//     },
//     7: {
//       contact_email: form.email,
//       esign_verified: form.aadhaarVerified ? 1 : 0, // ✅ Set 1 after verification
//       is_verified: 7,
//       pincode: pincode,
//       role: role,
//     },
//   };

//   // const verifyAadhaar = async () => {
//   //   if (!form.aadhaarLast4 || form.aadhaarLast4.length !== 4) {
//   //     toast.error("Enter valid last 4-digit Aadhaar");
//   //     return false;
//   //   }

//   //   try {
//   //     setLoading(true);
//   //     const res = await fetch(`${BASE_URL}upload-for-esign/${role}/${user.id}`, {
//   //       method: "POST",
//   //       headers: { "Content-Type": "application/json" },
//   //       body: JSON.stringify({ aadhaar_last_four_digit: form.aadhaarLast4 }),
//   //     });

//   //     const data = await res.json();

//   //     if (!data.status) {
//   //       toast.error(data.message || "Aadhaar verification failed");
//   //       setForm((prev) => ({ ...prev, aadhaarVerified: false }));
//   //       return false;
//   //     }

//   //     toast.success("Aadhaar verified successfully!");
//   //     setForm((prev) => ({ ...prev, aadhaarVerified: true }));
//   //     return true;
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const verifyAadhaar = async () => {
//   if (!form.aadhaarLast4 || form.aadhaarLast4.length !== 4) {
//     toast.error("Enter valid last 4 digits");
//     return;
//   }

//   const openCenteredPopup = (url, width = 600, height = 600) => {
//     const left = (window.innerWidth - width) / 2;
//     const top = (window.innerHeight - height) / 2;
//     window.open(
//       url,
//       "_blank",
//       `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
//     );
//   };

//   try {
//     setAadhaarStatus("loading");

//     // ✅ Get verificationId from session
//     const sessionData = localStorage.getItem("authUserData");
//     if (!sessionData) return;

//     const retailer = JSON.parse(sessionData);
//     const esignData = retailer?.esign_json ? JSON.parse(retailer.esign_json) : null;
//     const verificationId = esignData?.verification_id;

//     if (verificationId) {
//       try{
//       setEsignStatusLoading(true);
//       // 🔹 Check e-Sign status first
//       const statusRes = await fetch(
//         `${SWAGGER_URL}/Cashfree/get-esign-status?verificationId=${verificationId}`,
//         {
//           method: "GET",
//           headers: {
//             "AppId": APPID,
//             "SecretKey": SecretKey,
//             "Content-Type": "application/json",
//           },
//         }
//       );

//       const statusData = await statusRes.json();

//       if (statusData.status === "SUCCESS") {
//         toast.success("Aadhaar e-Sign already completed!");
//         setForm(prev => ({ ...prev, aadhaarVerified: true }));
//         setAadhaarStatus("success");
//         return;
//       }

//       if (statusData.status === "IN Progress") {
//         const signingLink =
//           statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

//         if (signingLink) {
//           toast.info("e-Sign in progress — opening signing window...");
//           openCenteredPopup(signingLink);
//           setAadhaarStatus("verifying");
//           return;
//         }
//       }
//     } finally {
//       setEsignStatusLoading(false); 
//     }
//   }

//     // 🔹 Initiate new eSign if not started
//     const res = await fetch(`${BASE_URL}upload-for-esign/${role}/${user.id}`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ aadhaar_last_four_digit: form.aadhaarLast4 }),
//     });

//     const data = await res.json();

//     // 🟠 Already initiated
//     if (data.message?.includes("Avoid duplicate requests") && data?.data?.esign_json) {
//       const esignData = JSON.parse(data.data.esign_json);
//       const signingLink = esignData?.signing_link;
//       if (signingLink) {
//         toast.success("eSign already initiated — opening window...");
//         openCenteredPopup(signingLink);
//         setAadhaarStatus("verifying");
//         return;
//       }
//       toast.error("Signing link not found.");
//       setAadhaarStatus("error");
//       return;
//     }

//     // 🟢 New eSign
//     if (data.status && data.esign_response) {
//       const signingLink = data.esign_response.signing_link;
//       toast.success("Opening e-Sign window...");
//       openCenteredPopup(signingLink);
//       setAadhaarStatus("verifying");
//       return;
//     }

//     toast.error(data.message || "Aadhaar verification failed");
//     setAadhaarStatus("error");

//   } catch (err) {
//     console.error(err);
//     toast.error("Something went wrong");
//     setAadhaarStatus("error");
//   }
// };

// // ✅ Automatic check when step 7 loads and auto-submit if eSign done
//   useEffect(() => {
//     const checkEsignStatusOnLoad = async () => {
//       if (currentStep !== 7) return;

//       const sessionData = localStorage.getItem("authUserData");
//       if (!sessionData) return;

//       let retailer;
//       try {
//         retailer = JSON.parse(sessionData);
//       } catch (err) {
//         console.error("Invalid session data", err);
//         return;
//       }

//       const esignData = retailer?.esign_json ? JSON.parse(retailer.esign_json) : null;
//       const verificationId = esignData?.verification_id;
//       if (!verificationId) return;

//       try {
//         setAadhaarStatus("loading");

//         const statusRes = await fetch(
//           `${SWAGGER_URL}/Cashfree/get-esign-status?verificationId=${verificationId}`,
//           {
//             method: "GET",
//             headers: {
//               "AppId": APPID,
//               "SecretKey": SecretKey,
//               "Content-Type": "application/json",
//             },
//           }
//         );

//         const statusData = await statusRes.json();

//         if (statusData.status === "SUCCESS") {
//           toast.success("Aadhaar e-Sign already completed!");
//           setForm(prev => ({ ...prev, aadhaarVerified: true }));
//           setAadhaarStatus("success");

//           // ✅ Auto-call update API for step 7
//           const success = await postStepUpdate(stepPayloads[7], "E-Sign Verification");
//           if (success) {
//             setSubmitted(true); // Move to SuccessSummary
//           }
//         } else if (statusData.status === "IN Progress") {
//           const signingLink =
//             statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

//           if (signingLink) {
//             toast.info("e-Sign in progress — opening signing window...");
//             const openCenteredPopup = (url, width = 600, height = 600) => {
//               const left = (window.innerWidth - width) / 2;
//               const top = (window.innerHeight - height) / 2;
//               window.open(
//                 url,
//                 "_blank",
//                 `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
//               );
//             };
//             openCenteredPopup(signingLink);
//             setAadhaarStatus("verifying");
//           }
//         } else {
//           setAadhaarStatus("error");
//         }
//       } catch (err) {
//         console.error(err);
//         toast.error("Failed to check e-Sign status");
//         setAadhaarStatus("error");
//       }
//     };

//     checkEsignStatusOnLoad();
//   }, [currentStep]);


//   // ------------------- NEXT BUTTON -------------------
// //  const handleNext = async () => {
// //   // ✅ Step validation before anything
// //   const isValid = validateStep();
// //   if (!isValid) {
// //     toast.error("Please complete all required fields before proceeding.");
// //     return; // Stop moving to next step
// //   }

// //   // ✅ Step 7: Aadhaar eSign must be complete
// //   if (currentStep === 7 && !form.aadhaarVerified) {
// //     toast.error("Complete Aadhaar e-Sign before proceeding.");
// //     return;
// //   }

// //   // --- KEEP ALL YOUR EXISTING STEP 6 AND NEXT LOGIC BELOW ---
// //   if (currentStep === 6) {
// //     const sessionData = localStorage.getItem("authUserData");
// //     if (sessionData) {
// //       const retailer = JSON.parse(sessionData);
// //       const esignData = retailer.esign_json ? JSON.parse(retailer.esign_json) : null;
// //       const verificationId = esignData?.verification_id;
// //       console.log("verification id: ", verificationId);

// //       if (verificationId) {
// //         try {
// //           setAadhaarStatus("loading");

// //           const res = await fetch(
// //             `https://agitated-cori.103-195-185-254.plesk.page/api/Cashfree/get-esign-status?verificationId=${verificationId}`,
// //             {
// //               method: "GET",
// //               headers: {
// //                 "AppId": APPID,
// //                 "SecretKey": SecretKey,
// //                 "Content-Type": "application/json",
// //               },
// //             }
// //           );

// //           const statusData = await res.json();

// //           if (statusData.status === "SUCCESS") {
// //             toast.success("Aadhaar e-Sign already completed!");
// //             setForm(prev => ({ ...prev, aadhaarVerified: true }));
// //             setAadhaarStatus("success");
// //           } else if (statusData.status === "IN Progress") {
// //             const signingLink =
// //               statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

// //             if (signingLink) {
// //               toast.info("e-Sign in progress — opening popup...");
// //               const openCenteredPopup = (url, width = 600, height = 600) => {
// //                 const left = (window.innerWidth - width) / 2;
// //                 const top = (window.innerHeight - height) / 2;
// //                 window.open(
// //                   url,
// //                   "_blank",
// //                   `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
// //                 );
// //               };
// //               openCenteredPopup(signingLink);
// //               setAadhaarStatus("verifying");
// //             }
// //           }
// //         } catch (err) {
// //           console.error(err);
// //           toast.error("Failed to check e-Sign status");
// //           setAadhaarStatus("error");
// //         }
// //       }
// //     }
// //   }

// //   // Move to next step only if all checks pass
// //   setCurrentStep(prev => prev + 1);
// // };

//   const handleNext = async () => {
//     // 1️⃣ Validate Step
//     const isValid = validateStep();
//     if (!isValid) {
//       toast.error("Please complete all required fields before proceeding.");
//       return;
//     }

//     // 2️⃣ Aadhaar step (step 7)
//     if (currentStep === 7 && !form.aadhaarVerified) {
//       toast.error("Complete Aadhaar e-Sign before proceeding.");
//       return;
//     }

//     // 3️⃣ API CALL for Current Step
//     const success = await postStepUpdate(stepPayloads[currentStep], `Step ${currentStep}`);
//     if (!success) return; // stop if API error

//     // 4️⃣ Move to next step
//     setCurrentStep(prev => prev + 1);
//   };


//   // ------------------- BACK BUTTON -------------------
//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((prev) => prev - 1);
//   };

//   return (
//     <div className="w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
//       <Header />

//       <div className="flex flex-col md:flex-row gap-6">
//         {/* Sidebar Stepper */}
//         <aside className="w-full md:w-60">
//           <Stepper steps={displaySteps} currentStep={currentStep} />
//         </aside>

//         {/* Main Content */}
//         <main className="flex-1">
//           {submitted ? (
//             <SuccessSummary form={form} />
//           ) : (
//             <>
//               <FormBody
//                 currentStep={currentStep}
//                 getDisplayStepNumber={getDisplayStepNumber}
//                 form={form}
//                 errors={errors}
//                 updateField={updateField}
//                 selectedPackage={selectedPackage}
//                 setSelectedPackage={setSelectedPackage}
//                 panApiResponse={panApiResponse}
//                 setPanApiResponse={setPanApiResponse}
//                 verifyAadhaar={verifyAadhaar} // ✅ Pass function
//                 aadhaarStatus={aadhaarStatus}
//                 setAadhaarStatus={setAadhaarStatus}
//                 setForm={setForm}
//                 esignStatusLoading={esignStatusLoading}
//                 setEsignStatusLoading={setEsignStatusLoading}
//                 setIsPaymentSuccess={setIsPaymentSuccess}
//                 gstVerified={gstVerified}
//                 setGstVerified={setGstVerified}
//                 gstResponse={gstResponse}
//                 setGstResponse={setGstResponse}
//                 bankVerified={bankVerified}
//                 setBankVerified={setBankVerified}
//                 bankResponse={bankResponse}
//                 setBankResponse={setBankResponse}

//               />
//               <Footer
//                 currentStep={currentStep}
//                 onBack={handleBack}
//                 onNext={handleNext}
//                 isLast={currentStep === steps.length}
//                 loading={loading}
//                 bankVerified={bankVerified}
//               />
//             </>
//           )}
//         </main>
//       </div>
//     </div>

//   );
// }


// ************************************************************Full Working Code with payment***************************************************************************


// import { useEffect, useState } from "react";
// import Header from "../../../../Component/RetailerComponents/Header";
// import Stepper from "../../../../Component/RetailerComponents/Stepper";
// import SuccessSummary from "../../../../Component/RetailerComponents/SuccessSummary";
// import FormBody from "../../../../Component/RetailerComponents/FormBody";
// import Footer from "../../../../Component/RetailerComponents/Footer";
// import toast from "react-hot-toast";
// import { APPID, BASE_URL, SecretKey } from "../../../../config/api";

// // Step Labels
// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "Payment" },
//   { id: 3, name: "PAN Verification" },
//   { id: 4, name: "GST Details" },
//   { id: 5, name: "Bank Details" },
//   { id: 6, name: " Agreement" },
//   { id: 7, name: "E-Sign Verify" }, // ✅ New last step
// ];


// export default function RetailerOnboarding() {
//   const user = JSON.parse(localStorage.getItem("authUserData") || "{}");

//   const isVerified = Number(user?.is_verified || 0);
//   const startingStep = isVerified > 0 ? isVerified + 1 : 1;
//   const initialStep = startingStep > steps.length ? steps.length : startingStep;

//   const [currentStep, setCurrentStep] = useState(initialStep);
//   const [submitted, setSubmitted] = useState(isVerified >= steps.length);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [selectedPackage, setSelectedPackage] = useState(null);
//   const [panApiResponse, setPanApiResponse] = useState(null);
//   const [aadhaarStatus, setAadhaarStatus] = useState("idle");
//   const [esignStatusLoading, setEsignStatusLoading] = useState(false);
//   const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);

//   const [gstVerified, setGstVerified] = useState(false);
//   const [gstResponse, setGstResponse] = useState(null);

//   const [bankVerified, setBankVerified] = useState(false);
//   const [bankResponse, setBankResponse] = useState(null);

//   const [form, setForm] = useState({
//     email: user?.contact_email || "",
//     phone: user?.phone || "",
//     paymentRef: "",
//     panNumber: "",
//     panVerified: false,
//     gstTreatment: "",
//     gstin: "",
//     businessName: "",
//     tradeName: "",
//     accountNumber: "",
//     ifsc: "",
//     bankName: "",
//     authorisedName: "",
//     agreed: false,
//     aadhaar: "",          // ✅ Add Aadhaar field
//     aadhaarVerified: false, // ✅ Track verification status
//   });


//   const address_line_1 = user?.address_line1;
//   const address_line_2 = user?.address_line2;
//   const state = user?.state;
//   const district = user?.district;
//   const city = user?.city;
//   const pincode = user?.pincode;
//   const role = user?.role;
//   const company_id = user?.company_id;

//   const sessionUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
 

//   const isUnregistered = form.gstTreatment === "unregistered";

//   const isPanAlreadyVerified =
//   Number(sessionUser?.pan_verified) === 1 || form.panVerified === true;

//   const updateField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   // Simple PAN validation (5 letters, 4 digits, 1 letter)
//   const isValidPAN = (pan) => {
//     const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
//     return panRegex.test(pan.toUpperCase());
//   };


//   // ------------------- VALIDATION -------------------
//   const validateStep = () => {
//     const newErrors = {};

//     if (currentStep === 1) {
//       if (!form.email) newErrors.email = "Enter email";
//       if (!form.phone) newErrors.phone = "Enter phone number";
//     }

//     if (currentStep === 3) {
//       if (!form.panNumber) newErrors.panNumber = "Enter valid PAN number";

//       if (!isPanAlreadyVerified && !panApiResponse && !form.panVerified) {
//         newErrors.panNumber = "Please verify PAN before proceeding";
//       }
//     }

//     if (currentStep === 4) {
//       if (!form.gstTreatment) newErrors.gstTreatment = "Select GST treatment";
//       if (form.gstTreatment !== "unregistered" && !gstVerified) {
//         newErrors.gstin = "Please verify GST before proceeding";
//       }
//       if (!form.businessName)
//         newErrors.businessName = "Enter legal business name";
//     }

//     if (currentStep === 5) {
//       if (!form.accountNumber) newErrors.accountNumber = "Enter account number";
//       if (!form.ifsc) newErrors.ifsc = "Enter IFSC code";
//       if (!form.bankName) newErrors.bankName = "Enter bank name";
//       if (!form.bankProofType) newErrors.bankProofType = "Select file type to upload";
//       if (!form.bankProofFile) newErrors.bankProofFile = "Select file to upload";
//       if (!form.authorisedName)
//         newErrors.authorisedName = "Authorised person required";

//       if (!bankVerified) {
//           newErrors.accountNumber = "Verify bank account before proceeding";
//         }
//     }

//     if (currentStep === 6) {
//       if (!form.agreed) newErrors.agreed = "You must accept the agreement";
//     }


//     if (currentStep === 7) {
//       if (!form.aadhaarLast4) {
//         newErrors.aadhaarLast4 = "Enter last 4 digits of Aadhaar";
//       } else if (!/^\d{4}$/.test(form.aadhaarLast4)) {
//         newErrors.aadhaarLast4 = "Aadhaar must be 4 digits";
//       }
//     }


//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // ------------------- API POST FUNCTION -------------------
//   // const postStepUpdate = async (payload, stepName) => {
//   //   try {
//   //     setLoading(true);
//   //     const res = await fetch(`${BASE_URL}common/update`, {
//   //       method: "POST",
//   //       headers: { "Content-Type": "application/json" },
//   //       body: JSON.stringify(payload),
//   //     });

//   //     const data = await res.json();

//   //     if (!res.ok || !data.status) {
//   //       toast.error(data.message || `${stepName} failed`);
//   //       return false;
//   //     }

//   //     toast.success(`${stepName} Saved`);
//   //     return true;
//   //   } catch {
//   //     toast.error("Something went wrong");
//   //     return false;
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const postStepUpdate = async (payload, stepName) => {
//   try {
//     setLoading(true);

//     const res = await fetch(`${BASE_URL}common/update`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     });

//     const data = await res.json();

//     if (!res.ok || !data.status) {
//       toast.error(data.message || `${stepName} failed`);
//       return false;
//     }

//     toast.success(`${stepName} Saved`);

//     // 🔥🔥 IMPORTANT: Update session so refresh shows updated step
//     if (data?.data) {
//       localStorage.setItem("authUserData", JSON.stringify(data.data));
//     }

//     return true;
//   } catch (err) {
//     toast.error("Something went wrong");
//     return false;
//   } finally {
//     setLoading(false);
//   }
// };


//   // ------------------- STEP PAYLOAD -------------------
//   const stepPayloads = {
//     1: {
//       contact_email: form.email,
//       phone: form.phone,
//       address_line1: address_line_1,
//       address_line2: address_line_2,
//       state: state,
//       district: district,
//       city: city,
//       pincode: pincode,
//       role: role,
//       company_id: company_id,
//       is_verified: 1,
//     },
//     2: {
//       contact_email: form.email,
//       payment_ref: form.paymentRef,
//       package_id: selectedPackage?.id,
//       package_name: selectedPackage?.package_name,
//       badge_name: selectedPackage?.badge?.name,
//       badge_id: selectedPackage?.badge?.id,
//       benefits: selectedPackage?.badge?.benefits,
//       eligibility: selectedPackage?.badge?.eligibility,
//       lead_amount: selectedPackage?.amount,
//       is_payment_success: isPaymentSuccess ? 1 : 0,
//       is_verified: 2,
//       pincode: pincode,
//       role: role,
//     },
//     3: {
//         contact_email: form.email,
//         pan: form.panNumber,
//         is_verified: 3,
//         pan_verified: isPanAlreadyVerified || panApiResponse ? 1 : 0,
//         pan_json: panApiResponse || sessionUser?.pan_json || null,
//         pincode: pincode,
//         role: role,
//       },
//     4: {
//         contact_email: form.email,
//         business_type: form.gstTreatment,
//         gst: form.gstin,
//         pan_number: isUnregistered ? form.gstin : "",

//         // ✅ GST verification flags
//         gst_verified: gstVerified ? 1 : 0,
//         gst_json: gstResponse ? gstResponse : null,

//         business_name: form.businessName,
//         trade_name: form.tradeName,

//         is_verified: 4,
//         pincode: pincode,
//         role: role,
//       },
//     5: {
//         contact_email: form.email,
//         account_type: form.accountType,
//         account_no: form.accountNumber,
//         ifsc_code: bankResponse?.ifsc_details?.ifsc || form.ifsc,
//         bank_name: bankResponse?.bank_name,
//         branch_name: bankResponse?.branch,
//         authorised_name: form.authorisedName,

//         bank_verified: bankVerified ? 1 : 0,
//         bank_json: bankResponse || null,

//         is_verified: 5,
//         pincode: pincode,
//         role: role,
//       },
//     6: {
//       contact_email: form.email,
//       is_verified: 6,
//       pincode: pincode,
//       role: role,
//     },
//     7: {
//       contact_email: form.email,
//       esign_verified: form.aadhaarVerified ? 1 : 0, // ✅ Set 1 after verification
//       is_verified: 7,
//       pincode: pincode,
//       role: role,
//     },
//   };

//   // const verifyAadhaar = async () => {
//   //   if (!form.aadhaarLast4 || form.aadhaarLast4.length !== 4) {
//   //     toast.error("Enter valid last 4-digit Aadhaar");
//   //     return false;
//   //   }

//   //   try {
//   //     setLoading(true);
//   //     const res = await fetch(`${BASE_URL}upload-for-esign/${role}/${user.id}`, {
//   //       method: "POST",
//   //       headers: { "Content-Type": "application/json" },
//   //       body: JSON.stringify({ aadhaar_last_four_digit: form.aadhaarLast4 }),
//   //     });

//   //     const data = await res.json();

//   //     if (!data.status) {
//   //       toast.error(data.message || "Aadhaar verification failed");
//   //       setForm((prev) => ({ ...prev, aadhaarVerified: false }));
//   //       return false;
//   //     }

//   //     toast.success("Aadhaar verified successfully!");
//   //     setForm((prev) => ({ ...prev, aadhaarVerified: true }));
//   //     return true;
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const verifyAadhaar = async () => {
//   if (!form.aadhaarLast4 || form.aadhaarLast4.length !== 4) {
//     toast.error("Enter valid last 4 digits");
//     return;
//   }

//   const openCenteredPopup = (url, width = 600, height = 600) => {
//     const left = (window.innerWidth - width) / 2;
//     const top = (window.innerHeight - height) / 2;
//     window.open(
//       url,
//       "_blank",
//       `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
//     );
//   };

//   try {
//     setAadhaarStatus("loading");

//     // ✅ Get verificationId from session
//     const sessionData = localStorage.getItem("authUserData");
//     if (!sessionData) return;

//     const retailer = JSON.parse(sessionData);
//     const esignData = retailer?.esign_json ? JSON.parse(retailer.esign_json) : null;
//     const verificationId = esignData?.verification_id;

//     if (verificationId) {
//       try{
//       setEsignStatusLoading(true);
//       // 🔹 Check e-Sign status first
//       const statusRes = await fetch(
//         `https://agitated-cori.103-195-185-254.plesk.page/api/Cashfree/get-esign-status?verificationId=${verificationId}`,
//         {
//           method: "GET",
//           headers: {
//             "AppId": APPID,
//             "SecretKey": SecretKey,
//             "Content-Type": "application/json",
//           },
//         }
//       );

//       const statusData = await statusRes.json();

//       if (statusData.status === "SUCCESS") {
//         toast.success("Aadhaar e-Sign already completed!");
//         setForm(prev => ({ ...prev, aadhaarVerified: true }));
//         setAadhaarStatus("success");
//         return;
//       }

//       if (statusData.status === "IN Progress") {
//         const signingLink =
//           statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

//         if (signingLink) {
//           toast.info("e-Sign in progress — opening signing window...");
//           openCenteredPopup(signingLink);
//           setAadhaarStatus("verifying");
//           return;
//         }
//       }
//     } finally {
//       setEsignStatusLoading(false); 
//     }
//   }

//     // 🔹 Initiate new eSign if not started
//     const res = await fetch(`${BASE_URL}upload-for-esign/${role}/${user.id}`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ aadhaar_last_four_digit: form.aadhaarLast4 }),
//     });

//     const data = await res.json();

//     // 🟠 Already initiated
//     if (data.message?.includes("Avoid duplicate requests") && data?.data?.esign_json) {
//       const esignData = JSON.parse(data.data.esign_json);
//       const signingLink = esignData?.signing_link;
//       if (signingLink) {
//         toast.success("eSign already initiated — opening window...");
//         openCenteredPopup(signingLink);
//         setAadhaarStatus("verifying");
//         return;
//       }
//       toast.error("Signing link not found.");
//       setAadhaarStatus("error");
//       return;
//     }

//     // 🟢 New eSign
//     if (data.status && data.esign_response) {
//       const signingLink = data.esign_response.signing_link;
//       toast.success("Opening e-Sign window...");
//       openCenteredPopup(signingLink);
//       setAadhaarStatus("verifying");
//       return;
//     }

//     toast.error(data.message || "Aadhaar verification failed");
//     setAadhaarStatus("error");

//   } catch (err) {
//     console.error(err);
//     toast.error("Something went wrong");
//     setAadhaarStatus("error");
//   }
// };

// // ✅ Automatic check when step 7 loads and auto-submit if eSign done
//   useEffect(() => {
//     const checkEsignStatusOnLoad = async () => {
//       if (currentStep !== 7) return;

//       const sessionData = localStorage.getItem("authUserData");
//       if (!sessionData) return;

//       let retailer;
//       try {
//         retailer = JSON.parse(sessionData);
//       } catch (err) {
//         console.error("Invalid session data", err);
//         return;
//       }

//       const esignData = retailer?.esign_json ? JSON.parse(retailer.esign_json) : null;
//       const verificationId = esignData?.verification_id;
//       if (!verificationId) return;

//       try {
//         setAadhaarStatus("loading");

//         const statusRes = await fetch(
//           `https://agitated-cori.103-195-185-254.plesk.page/api/Cashfree/get-esign-status?verificationId=${verificationId}`,
//           {
//             method: "GET",
//             headers: {
//               "AppId": APPID,
//               "SecretKey": SecretKey,
//               "Content-Type": "application/json",
//             },
//           }
//         );

//         const statusData = await statusRes.json();

//         if (statusData.status === "SUCCESS") {
//           toast.success("Aadhaar e-Sign already completed!");
//           setForm(prev => ({ ...prev, aadhaarVerified: true }));
//           setAadhaarStatus("success");

//           // ✅ Auto-call update API for step 7
//           const success = await postStepUpdate(stepPayloads[7], "E-Sign Verification");
//           if (success) {
//             setSubmitted(true); // Move to SuccessSummary
//           }
//         } else if (statusData.status === "IN Progress") {
//           const signingLink =
//             statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

//           if (signingLink) {
//             toast.info("e-Sign in progress — opening signing window...");
//             const openCenteredPopup = (url, width = 600, height = 600) => {
//               const left = (window.innerWidth - width) / 2;
//               const top = (window.innerHeight - height) / 2;
//               window.open(
//                 url,
//                 "_blank",
//                 `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
//               );
//             };
//             openCenteredPopup(signingLink);
//             setAadhaarStatus("verifying");
//           }
//         } else {
//           setAadhaarStatus("error");
//         }
//       } catch (err) {
//         console.error(err);
//         toast.error("Failed to check e-Sign status");
//         setAadhaarStatus("error");
//       }
//     };

//     checkEsignStatusOnLoad();
//   }, [currentStep]);


//   // ------------------- NEXT BUTTON -------------------
// //  const handleNext = async () => {
// //   // ✅ Step validation before anything
// //   const isValid = validateStep();
// //   if (!isValid) {
// //     toast.error("Please complete all required fields before proceeding.");
// //     return; // Stop moving to next step
// //   }

// //   // ✅ Step 7: Aadhaar eSign must be complete
// //   if (currentStep === 7 && !form.aadhaarVerified) {
// //     toast.error("Complete Aadhaar e-Sign before proceeding.");
// //     return;
// //   }

// //   // --- KEEP ALL YOUR EXISTING STEP 6 AND NEXT LOGIC BELOW ---
// //   if (currentStep === 6) {
// //     const sessionData = localStorage.getItem("authUserData");
// //     if (sessionData) {
// //       const retailer = JSON.parse(sessionData);
// //       const esignData = retailer.esign_json ? JSON.parse(retailer.esign_json) : null;
// //       const verificationId = esignData?.verification_id;
// //       console.log("verification id: ", verificationId);

// //       if (verificationId) {
// //         try {
// //           setAadhaarStatus("loading");

// //           const res = await fetch(
// //             `https://agitated-cori.103-195-185-254.plesk.page/api/Cashfree/get-esign-status?verificationId=${verificationId}`,
// //             {
// //               method: "GET",
// //               headers: {
// //                 "AppId": APPID,
// //                 "SecretKey": SecretKey,
// //                 "Content-Type": "application/json",
// //               },
// //             }
// //           );

// //           const statusData = await res.json();

// //           if (statusData.status === "SUCCESS") {
// //             toast.success("Aadhaar e-Sign already completed!");
// //             setForm(prev => ({ ...prev, aadhaarVerified: true }));
// //             setAadhaarStatus("success");
// //           } else if (statusData.status === "IN Progress") {
// //             const signingLink =
// //               statusData.signers?.[0]?.meta_data?.signing_link || statusData.signing_link;

// //             if (signingLink) {
// //               toast.info("e-Sign in progress — opening popup...");
// //               const openCenteredPopup = (url, width = 600, height = 600) => {
// //                 const left = (window.innerWidth - width) / 2;
// //                 const top = (window.innerHeight - height) / 2;
// //                 window.open(
// //                   url,
// //                   "_blank",
// //                   `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=no,status=no`
// //                 );
// //               };
// //               openCenteredPopup(signingLink);
// //               setAadhaarStatus("verifying");
// //             }
// //           }
// //         } catch (err) {
// //           console.error(err);
// //           toast.error("Failed to check e-Sign status");
// //           setAadhaarStatus("error");
// //         }
// //       }
// //     }
// //   }

// //   // Move to next step only if all checks pass
// //   setCurrentStep(prev => prev + 1);
// // };

//   const handleNext = async () => {
//     // 1️⃣ Validate Step
//     const isValid = validateStep();
//     if (!isValid) {
//       toast.error("Please complete all required fields before proceeding.");
//       return;
//     }

//     // 2️⃣ Aadhaar step (step 7)
//     if (currentStep === 7 && !form.aadhaarVerified) {
//       toast.error("Complete Aadhaar e-Sign before proceeding.");
//       return;
//     }

//     // 3️⃣ API CALL for Current Step
//     const success = await postStepUpdate(stepPayloads[currentStep], `Step ${currentStep}`);
//     if (!success) return; // stop if API error

//     // 4️⃣ Move to next step
//     setCurrentStep(prev => prev + 1);
//   };


//   // ------------------- BACK BUTTON -------------------
//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((prev) => prev - 1);
//   };

//   return (
//     <div className="w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
//       <Header />

//       <div className="flex flex-col md:flex-row gap-6">
//         {/* Sidebar Stepper */}
//         <aside className="w-full md:w-60">
//           <Stepper steps={steps} currentStep={currentStep} />
//         </aside>

//         {/* Main Content */}
//         <main className="flex-1">
//           {submitted ? (
//             <SuccessSummary form={form} />
//           ) : (
//             <>
//               <FormBody
//                 currentStep={currentStep}
//                 form={form}
//                 errors={errors}
//                 updateField={updateField}
//                 selectedPackage={selectedPackage}
//                 setSelectedPackage={setSelectedPackage}
//                 panApiResponse={panApiResponse}
//                 setPanApiResponse={setPanApiResponse}
//                 verifyAadhaar={verifyAadhaar} // ✅ Pass function
//                 aadhaarStatus={aadhaarStatus}
//                 setAadhaarStatus={setAadhaarStatus}
//                 setForm={setForm}
//                 esignStatusLoading={esignStatusLoading}
//                 setEsignStatusLoading={setEsignStatusLoading}
//                 setIsPaymentSuccess={setIsPaymentSuccess}
//                 gstVerified={gstVerified}
//                 setGstVerified={setGstVerified}
//                 gstResponse={gstResponse}
//                 setGstResponse={setGstResponse}
//                 bankVerified={bankVerified}
//                 setBankVerified={setBankVerified}
//                 bankResponse={bankResponse}
//                 setBankResponse={setBankResponse}

//               />
//               <Footer
//                 currentStep={currentStep}
//                 onBack={handleBack}
//                 onNext={handleNext}
//                 isLast={currentStep === steps.length}
//                 loading={loading}
//                 bankVerified={bankVerified}
//               />
//             </>
//           )}
//         </main>
//       </div>
//     </div>

//   );
// }

// *********************************************************************************************************************************************************************


// import { useEffect, useState } from "react";
// import Header from "../../../../Component/RetailerComponents/Header";
// import Stepper from "../../../../Component/RetailerComponents/Stepper";
// import SuccessSummary from "../../../../Component/RetailerComponents/SuccessSummary";
// import FormBody from "../../../../Component/RetailerComponents/FormBody";
// import Footer from "../../../../Component/RetailerComponents/Footer";
// import toast from "react-hot-toast";
// import { BASE_URL } from "../../../../config/api";

// // Step Labels
// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "Payment" },
//   { id: 3, name: "PAN Verification" }, // NEW
//   { id: 4, name: "GST Details" },
//   { id: 5, name: "Bank Details" },
//   { id: 6, name: "Sign Agreement" },
// ];


// export default function RetailerOnboarding() {
//   const user = JSON.parse(localStorage.getItem("authUserData") || "{}");

//   const isVerified = Number(user?.is_verified || 0);

//   // Determine step user should start from
//   const startingStep = isVerified > 0 ? isVerified + 1 : 1;
//   const initialStep = startingStep > steps.length ? steps.length : startingStep;

//   const [currentStep, setCurrentStep] = useState(initialStep);
//   const [submitted, setSubmitted] = useState(isVerified >= steps.length);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [selectedPackage, setSelectedPackage] = useState(null);
//   // Form Fields
//   const [form, setForm] = useState({
//     email: user?.contact_email || "",
//     phone: user?.phone || "",
//     paymentRef: "",
//     panNumber: "",
//     panVerified: false,    // <-- added
//     gstTreatment: "",
//     gstin: "",
//     businessName: "",
//     tradeName: "",
//     accountNumber: "",
//     ifsc: "",
//     bankName: "",
//     authorisedName: "",
//     agreed: false,
//   });


//   const address_line_1 = user?.address_line1;
//   const address_line_2 = user?.address_line2;
//   const state = user?.state;
//   const district = user?.district;
//   const city = user?.city;
//   const pincode = user?.pincode;
//   const role = user?.role;
//   const company_id = user?.company_id;

//   const isUnregistered = form.gstTreatment === "unregistered";

//   const updateField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   // ------------------- VALIDATION -------------------
//   const validateStep = () => {
//     const newErrors = {};

//     if (currentStep === 1) {
//       if (!form.email) newErrors.email = "Enter email";
//       if (!form.phone) newErrors.phone = "Enter phone number";
//     }

//     if (currentStep === 3) {
//       if (!form.panNumber) newErrors.panNumber = "Enter valid PAN number";
//     }


//     if (currentStep === 4) {
//       if (!form.gstTreatment) newErrors.gstTreatment = "Select GST treatment";
//       if (!form.gstin)
//         newErrors.gstin = isUnregistered ? "Enter PAN number" : "Enter GSTIN / UIN";
//       if (!form.businessName)
//         newErrors.businessName = "Enter legal business name";
//     }

//     if (currentStep === 5) {
//       if (!form.accountNumber) newErrors.accountNumber = "Enter account number";
//       if (!form.ifsc) newErrors.ifsc = "Enter IFSC code";
//       if (!form.bankName) newErrors.bankName = "Enter bank name";
//       if (!form.authorisedName)
//         newErrors.authorisedName = "Authorised person required";
//     }

//     if (currentStep === 6) {
//       if (!form.agreed) newErrors.agreed = "You must accept the agreement";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // ------------------- API POST FUNCTION -------------------
//   const postStepUpdate = async (payload, stepName) => {
//     try {
//       setLoading(true);
//       const res = await fetch(`${BASE_URL}common/update`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       });

//       const data = await res.json();

//       if (!res.ok || !data.status) {
//         toast.error(data.message || `${stepName} failed`);
//         return false;
//       }

//       toast.success(`${stepName} Saved`);
//       return true;
//     } catch {
//       toast.error("Something went wrong");
//       return false;
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ------------------- STEP PAYLOAD -------------------
//   const stepPayloads = {
//     1: {
//       contact_email: form.email,
//       phone: form.phone,
//       address_line1: address_line_1,
//       address_line2: address_line_2,
//       state: state,
//       district: district,
//       city: city,
//       pincode: pincode,
//       role: role,
//       company_id:company_id,
//       is_verified: 1,
//     },
//     2: {
//         contact_email: form.email,
//         payment_ref: form.paymentRef,
//         package_id: selectedPackage?.id,
//         package_name: selectedPackage?.package_name,
//         badge_name: selectedPackage?.badge.name,
//         badge_id: selectedPackage?.badge?.id,
//         benefits: selectedPackage?.badge?.benefits,
//         eligibility: selectedPackage?.badge?.eligibility,
//         lead_amount: selectedPackage?.amount,
//         is_payment_success: true,
//         is_verified: 2,
//       },

//     3: {
//       contact_email: form.email,
//       pan_number: form.panNumber,
//       pan_verified: ,
//       pan_json : ,
//       is_verified: 3,
//     },
//     4: {
//       contact_email: form.email,
//       business_type: form.gstTreatment,
//       gst_number: form.gstin,
//       pan_number: isUnregistered ? form.gstin : "",
//       business_name: form.businessName,
//       trade_name: form.tradeName,
//       is_verified: 3,
//     },
//     5: {
//       contact_email: form.email,
//       account_type: form.accountType,
//       account_no: form.accountNumber,
//       ifsc: form.ifsc,
//       bank_name: form.bankName,
//       branch_name:form.branchname,
//       authorised_name: form.authorisedName,
//       is_verified: 4,
//     },
//     6: {
//       contact_email: form.email,
//       Isverified: true,
//       Is_agreement_signed: true,
//       is_verified: 5,
//     },
//   };

//   // ------------------- NEXT BUTTON -------------------
//   const handleNext = async () => {
//     if (!validateStep()) return;

//     if (currentStep === 3 && !form.authorisedName && form.businessName) {
//       setForm((prev) => ({ ...prev, authorisedName: prev.businessName }));
//     }

//     const payload = stepPayloads[currentStep];
//     const success = await postStepUpdate(payload, `Step ${currentStep}`);

//     if (!success) return;

//     // Update session storage progress
//     const updatedUser = { ...user, is_verified: currentStep };
//     localStorage.setItem("authUserData", JSON.stringify(updatedUser));

//     // Final Step
//     if (currentStep === steps.length) {
//       toast.success("Retailer onboarding completed 🎉");
//       setSubmitted(true);
//       return;
//     }

//     setCurrentStep((prev) => prev + 1);
//   };

//   // ------------------- BACK BUTTON -------------------
//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((prev) => prev - 1);
//   };

//   return (
//     <div className="w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
//       <Header />
//       <Stepper steps={steps} currentStep={currentStep} />

//       {submitted ? (
//         <SuccessSummary form={form} />
//       ) : (
//         <>
//           <FormBody
//             currentStep={currentStep}
//             form={form}
//             errors={errors}
//             updateField={updateField}
//             selectedPackage={selectedPackage}
//             setSelectedPackage={setSelectedPackage}
//           />


//           <Footer
//             currentStep={currentStep}
//             onBack={handleBack}
//             onNext={handleNext}
//             isLast={currentStep === steps.length}
//             loading={loading}
//           />
//         </>
//       )}
//     </div>
//   );
// }




// import { useState } from "react";
// import Header from "../../../../Component/RetailerComponents/Header";
// import Stepper from "../../../../Component/RetailerComponents/Stepper";
// import SuccessSummary from "../../../../Component/RetailerComponents/SuccessSummary";
// import FormBody from "../../../../Component/RetailerComponents/FormBody";
// import Footer from "../../../../Component/RetailerComponents/Footer";
// import toast from "react-hot-toast";

// // Step Display Labels
// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "Payment" },
//   { id: 3, name: "GST Details" },
//   { id: 4, name: "Bank Details" },
//   { id: 5, name: "Sign Agreement" },
// ];

// export default function RetailerOnboarding() {
//   const [currentStep, setCurrentStep] = useState(1);
//   const [submitted, setSubmitted] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);

//   const [form, setForm] = useState({
//     email: "",
//     phone: "",
//     paymentRef: "",
//     gstTreatment: "",
//     gstin: "",
//     businessName: "",
//     tradeName: "",
//     accountNumber: "",
//     ifsc: "",
//     bankName: "",
//     authorisedName: "",
//     agreed: false,
//   });

//   const user = localStorage.getItem("authUserData");
//   const address_line_1 = user.address_line1;
//   const address_line_2 = user.address_line2;
//   const state = user.state;
//   const district = user.district;
//   const city = user.city;
//   const pincode = user.pincode;
//   const Isverified = user.Isverified;
//   const role = user.role;
//   const company_id = user.company_id;


//   const isUnregistered = form.gstTreatment === "unregistered";

//   const updateField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   // ---------------------- VALIDATION ----------------------
//   const validateStep = () => {
//     const newErrors = {};

//     if (currentStep === 1) {
//       if (!form.email) newErrors.email = "Enter email";
//       if (!form.phone) newErrors.phone = "Enter phone number";
//     }

//     if (currentStep === 3) {
//       if (!form.gstTreatment) newErrors.gstTreatment = "Select GST treatment";
//       if (!form.gstin)
//         newErrors.gstin = isUnregistered ? "Enter PAN number" : "Enter GSTIN / UIN";

//       if (!form.businessName)
//         newErrors.businessName = "Enter legal business name";
//     }

//     if (currentStep === 4) {
//       if (!form.accountNumber) newErrors.accountNumber = "Enter account number";
//       if (!form.ifsc) newErrors.ifsc = "Enter IFSC code";
//       if (!form.bankName) newErrors.bankName = "Enter bank name";
//       if (!form.authorisedName)
//         newErrors.authorisedName = "Authorised person required";
//     }

//     if (currentStep === 5) {
//       if (!form.agreed) newErrors.agreed = "You must accept the agreement";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // ---------------------- SHARED API FUNCTION ----------------------
//   const postStepUpdate = async (payload, stepName) => {
//     try {
//       setLoading(true);
//       const res = await fetch("${BASE_URL}/common/update", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       });

//       const data = await res.json();

//       if (!res.ok || !data.status) {
//         toast.error(data.message || `${stepName} failed`);
//         return false;
//       }

//       toast.success(`${stepName} Saved`);
//       return true;
//     } catch (error) {
//       toast.error("Something went wrong");
//       return false;
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ---------------------- STEP-PAYLOAD LIST ----------------------
//   const stepPayloads = {
//     1: {
//       contact_email: form.email,
//       phone: form.phone,
//       address_line1: address_line_1,
//       address_line2: address_line_2, 
//       state: state,
//       district: district, 
//       city: city,
//       pincode: pincode,
//       role: role,
//       company_id: company_id,
//       is_verified: true,
//     },
//     2: {
//       contact_email: form.email,
//       payment_ref: form.paymentRef,
//       is_payment_success: true,
//     },
//     3: {
//       contact_email: form.email,
//       business_type: form.gstTreatment,
//       gst_number: form.gstin,
//       pan_number: isUnregistered ? form.gstin : "",
//       business_name: form.businessName,
//       trade_name: form.tradeName,
//     },
//     4: {
//       contact_email: form.email,
//       account_no: form.accountNumber,
//       ifsc: form.ifsc,
//       bank_name: form.bankName,
//       authorised_name: form.authorisedName,
//     },
//     5: {
//       contact_email: form.email,
//       Isverified: true,
//       Is_agreement_signed: true,
//     },
//   };

//   // ---------------------- NEXT STEP HANDLER ----------------------
//   const handleNext = async () => {
//     if (!validateStep()) return;

//     // Auto-fill authorised name when entering Bank Details
//     if (currentStep === 3 && !form.authorisedName && form.businessName) {
//       setForm((prev) => ({ ...prev, authorisedName: prev.businessName }));
//     }

//     const payload = stepPayloads[currentStep];
//     const success = await postStepUpdate(payload, `Step ${currentStep}`);

//     if (!success) return;

//     // FINAL STEP COMPLETE
//     if (currentStep === steps.length) {
//       toast.success("Retailer onboarding completed 🎉");
//       setSubmitted(true);
//       return;
//     }

//     setCurrentStep((step) => step + 1);
//   };

//   // ---------------------- BACK BUTTON ----------------------
//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((step) => step - 1);
//   };

//   return (
//     <div className="w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
//       <Header />
//       <Stepper steps={steps} currentStep={currentStep} />

//       {submitted ? (
//         <SuccessSummary form={form} />
//       ) : (
//         <>
//           <FormBody
//             currentStep={currentStep}
//             form={form}
//             errors={errors}
//             updateField={updateField}
//           />

//           <Footer
//             currentStep={currentStep}
//             onBack={handleBack}
//             onNext={handleNext}
//             isLast={currentStep === steps.length}
//             loading={loading}
//           />
//         </>
//       )}
//     </div>
//   );
// }



// import { useState } from "react";
// import Header from "../../../../Component/RetailerComponents/Header";
// import Stepper from "../../../../Component/RetailerComponents/Stepper";
// import SuccessSummary from "../../../../Component/RetailerComponents/SuccessSummary";
// import FormBody from "../../../../Component/RetailerComponents/FormBody";
// import Footer from "../../../../Component/RetailerComponents/Footer";
// import toast from "react-hot-toast";

// // 🔄 UPDATED STEP ORDER
// // Step 2 is now "Payment"
// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "Payment" },           // ⬅ moved here
//   { id: 3, name: "GST Details" },
//   { id: 4, name: "Bank Details" },
//   { id: 5, name: "Sign Agreement" },
// ];

// export default function RetailerOnboarding() {
//   const [currentStep, setCurrentStep] = useState(1);
//   const [submitted, setSubmitted] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);

//   const [form, setForm] = useState({
//     email: "",
//     phone: "",
//     paymentRef: "",     // 🔄 NEW FIELD FOR STEP 2
//     gstTreatment: "",
//     gstin: "",
//     businessName: "",
//     tradeName: "",
//     accountNumber: "",
//     ifsc: "",
//     bankName: "",
//     authorisedName: "",
//     agreed: false,
//   });

//   const isUnregistered = form.gstTreatment === "unregistered";

//   const updateField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   // 🔄 UPDATED VALIDATION BASED ON NEW STEP ORDER
//   const validateStep = () => {
//   const newErrors = {};

//   if (currentStep === 1) {
//     if (!form.email) newErrors.email = "Enter email";
//     if (!form.phone) newErrors.phone = "Enter phone number";
//   }

//   // ⛔ Step 2 → No validation required now
//   else if (currentStep === 2) {
//     // SKIP VALIDATION (leave empty)
//   }

//   else if (currentStep === 3) {
//     if (!form.gstTreatment) newErrors.gstTreatment = "Select GST treatment";
//     if (!form.gstin)
//       newErrors.gstin = isUnregistered ? "Enter PAN number" : "Enter GSTIN / UIN";

//     if (!form.businessName)
//       newErrors.businessName = "Enter legal business name";
//   }

//   else if (currentStep === 4) {
//     if (!form.accountNumber) newErrors.accountNumber = "Enter account number";
//     if (!form.ifsc) newErrors.ifsc = "Enter IFSC code";
//     if (!form.bankName) newErrors.bankName = "Enter bank name";
//     if (!form.authorisedName)
//       newErrors.authorisedName = "Authorised person is required";
//   }

//   else if (currentStep === 5) {
//     if (!form.agreed) newErrors.agreed = "You must accept the agreement";
//   }

//   setErrors(newErrors);
//   return Object.keys(newErrors).length === 0;
// };


//   const handleNext = async () => {
//     if (!validateStep()) return;

//     // Auto-fill authorised name when entering Bank Details
//     if (currentStep === 3 && !form.authorisedName && form.businessName) {
//       setForm((prev) => ({ ...prev, authorisedName: prev.businessName }));
//     }

//     // Final Step → Submit
//     if (currentStep === steps.length) {
//       setLoading(true);

//       try {
//         const payload = {
//           company_id: 1,
//           business_name: form.businessName,
//           owner_name: form.authorisedName,
//           email: form.email,
//           phone: form.phone,
//           password: "123456",
//           address_line1: form.address_line1 || "",
//           address_line2: form.address_line2 || "",
//           city: form.city || "",
//           state: form.state || "",
//           district: form.district || "",
//           pincode: form.pincode || "",
//           bank_name: form.bankName,
//           account_no: form.accountNumber,
//           ifsc_code: form.ifsc,
//           branch_name: form.branchName || "",
//           gst_number: form.gstin,
//           pan_number: isUnregistered ? form.gstin : form.panNumber || "",
//           business_type: isUnregistered ? "Unregister" : "Registered",
//           trade_name: form.tradeName || "",
//           payment_ref: form.paymentRef,   // 🔄 Added
//         };

//         const response = await fetch(
//           "${BASE_URL}/retailer/store",
//           {
//             method: "POST",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify(payload),
//           }
//         );

//         const res = await response.json();

//         if (response.ok && res.status) {
//           toast.success(res.message || "Retailer created successfully!");
//           setSubmitted(true);
//         } else {
//           toast.error(res.message || "Failed to create retailer");
//         }
//       } catch (error) {
//         toast.error("Something went wrong! Please try again");
//         console.error(error);
//       }

//       setLoading(false);
//       return;
//     }

//     // Move to next step
//     setCurrentStep((step) => step + 1);
//   };

//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((step) => step - 1);
//   };

//   return (
//     <div className="w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
//       <Header />
//       <Stepper steps={steps} currentStep={currentStep} />

//       {submitted ? (
//         <SuccessSummary form={form} />
//       ) : (
//         <>
//           <FormBody
//             currentStep={currentStep}
//             form={form}
//             errors={errors}
//             updateField={updateField}
//           />

//           <Footer
//             currentStep={currentStep}
//             onBack={handleBack}
//             onNext={handleNext}
//             isLast={currentStep === steps.length}
//             loading={loading}
//           />
//         </>
//       )}
//     </div>
//   );
// }




// import { useState } from "react";
// import Header from "../../../../Component/RetailerComponents/Header";
// import Stepper from "../../../../Component/RetailerComponents/Stepper";
// import SuccessSummary from "../../../../Component/RetailerComponents/SuccessSummary";
// import FormBody from "../../../../Component/RetailerComponents/FormBody";
// import Footer from "../../../../Component/RetailerComponents/Footer";
// import toast from "react-hot-toast";



// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "GST Details" },
//   { id: 3, name: "Bank Details" },
//   { id: 4, name: "Sign Agreement" },
//   { id: 5, name: "Payment" },
// ];

// export default function RetailerOnboarding() {
//   const [currentStep, setCurrentStep] = useState(1);
//   const [submitted, setSubmitted] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
  
//   const [form, setForm] = useState({
//     email: "",
//     phone: "",
//     gstTreatment: "",
//     gstin: "",
//     businessName: "",
//     tradeName: "",
//     accountNumber: "",
//     ifsc: "",
//     bankName: "",
//     authorisedName: "", // auto-fetched / prefilled
//     agreed: false,
//   });

//   const isUnregistered = form.gstTreatment === "unregistered";

//   const updateField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   const validateStep = () => {
//     const newErrors = {};

//     if (currentStep === 1) {
//       if (!form.email) newErrors.email = "Enter email";
//       if (!form.phone) newErrors.phone = "Enter phone number";
//     } else if (currentStep === 2) {
//       if (!form.gstTreatment) newErrors.gstTreatment = "Select GST treatment";
//       if (!form.gstin)
//         newErrors.gstin =
//           form.gstTreatment === "unregistered"
//             ? "Enter PAN number"
//             : "Enter GSTIN / UIN";
//       if (!form.businessName) newErrors.businessName = "Enter legal business name";
//     } else if (currentStep === 3) {
//       if (!form.accountNumber) newErrors.accountNumber = "Enter account number";
//       if (!form.ifsc) newErrors.ifsc = "Enter IFSC code";
//       if (!form.bankName) newErrors.bankName = "Enter bank name";
//       if (!form.authorisedName)
//         newErrors.authorisedName = "Authorised person is required";
//     } else if (currentStep === 4) {
//       if (!form.agreed) newErrors.agreed = "You must accept the agreement";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleNext = async () => {
//     if (!validateStep()) return;

//     // Auto-fill authorised person from business name when going from GST → Bank
//     if (currentStep === 2 && !form.authorisedName && form.businessName) {
//       setForm((prev) => ({ ...prev, authorisedName: prev.businessName }));
//     }

//     // If it's the last step, call API
//     if (currentStep === steps.length) {
//       setLoading(true); // start loading
//       try {
//         const payload = {
//           company_id: 1,
//           business_name: form.businessName,
//           owner_name: form.authorisedName,
//           email: form.email,
//           phone: form.phone,
//           password: "123456", // You can make it dynamic later
//           address_line1: form.address_line1 || "",
//           address_line2: form.address_line2 || "",
//           city: form.city || "",
//           state: form.state || "",
//           district: form.district || "",
//           pincode: form.pincode || "",
//           bank_name: form.bankName,
//           account_no: form.accountNumber,
//           ifsc_code: form.ifsc,
//           branch_name: form.branchName || "",
//           gst_number: form.gstin,
//           pan_number: isUnregistered ? form.gstin : form.panNumber || "",
//           business_type: isUnregistered ? "Unregister" : "Registered",
//           trade_name: form.tradeName || "",
//         };

//         const response = await fetch(
//           "${BASE_URL}/retailer/store",
//           {
//             method: "POST",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify(payload),
//           }
//         );

//         const res = await response.json();

//         if (response.ok && res.status) {
//           toast.success(res.message || "Retailer created successfully!");
//           setSubmitted(true);
//         } else {
//           toast.error(res.message || "Failed to create retailer");
//         }
//       } catch (error) {
//         toast.error("Something went wrong! Please try again");
//         console.error(error);
//       }
//       setLoading(false); // stop loading
//       return; // stop further execution
//     }

//     // Normal flow: move to next step
//     setCurrentStep((step) => step + 1);
//   };

//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((step) => step - 1);
//   };

//   return (
//     // <div className="min-h-screen bg-slate-50 flex justify-center py-8">
//       <div className="w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
//         <Header />
//         <Stepper steps={steps} currentStep={currentStep} />

//         {submitted ? (
//           <SuccessSummary form={form} />
//         ) : (
//           <>
//             <FormBody
//               currentStep={currentStep}
//               form={form}
//               errors={errors}
//               updateField={updateField}
//             />
//             <Footer
//               currentStep={currentStep}
//               onBack={handleBack}
//               onNext={handleNext}
//               isLast={currentStep === steps.length}
//               loading={loading}
//             />
//           </>
//         )}
//       </div>
//     // </div>
//   );
// }



// import { useState } from "react";
// import Header from "../../../../Component/RetailerComponents/Header";
// import Stepper from "../../../../Component/RetailerComponents/Stepper";
// import SuccessSummary from "../../../../Component/RetailerComponents/SuccessSummary";
// import FormBody from "../../../../Component/RetailerComponents/FormBody";
// import Footer from "../../../../Component/RetailerComponents/Footer";


// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "GST Details" },
//   { id: 3, name: "Bank Details" },
//   { id: 4, name: "Sign Agreement" },
//   { id: 5, name: "Payment" },
// ];

// export default function RetailerOnboarding() {
//   const [currentStep, setCurrentStep] = useState(1);
//   const [submitted, setSubmitted] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [form, setForm] = useState({
//     email: "",
//     phone: "",
//     gstTreatment: "",
//     gstin: "",
//     businessName: "",
//     tradeName: "",
//     accountNumber: "",
//     ifsc: "",
//     bankName: "",
//     authorisedName: "", // auto-fetched / prefilled
//     agreed: false,
//   });

//   const updateField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   const validateStep = () => {
//     const newErrors = {};

//     if (currentStep === 1) {
//       if (!form.email) newErrors.email = "Enter email";
//       if (!form.phone) newErrors.phone = "Enter phone number";
//     } else if (currentStep === 2) {
//       if (!form.gstTreatment) newErrors.gstTreatment = "Select GST treatment";
//       if (!form.gstin)
//         newErrors.gstin =
//           form.gstTreatment === "unregistered"
//             ? "Enter PAN number"
//             : "Enter GSTIN / UIN";
//       if (!form.businessName) newErrors.businessName = "Enter legal business name";
//     } else if (currentStep === 3) {
//       if (!form.accountNumber) newErrors.accountNumber = "Enter account number";
//       if (!form.ifsc) newErrors.ifsc = "Enter IFSC code";
//       if (!form.bankName) newErrors.bankName = "Enter bank name";
//       if (!form.authorisedName)
//         newErrors.authorisedName = "Authorised person is required";
//     } else if (currentStep === 4) {
//       if (!form.agreed) newErrors.agreed = "You must accept the agreement";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleNext = () => {
//     if (!validateStep()) return;

//     // Auto-fill authorised person from business name when going from GST → Bank
//     if (currentStep === 2 && !form.authorisedName && form.businessName) {
//       setForm((prev) => ({ ...prev, authorisedName: prev.businessName }));
//     }

//     if (currentStep < steps.length) {
//       setCurrentStep((step) => step + 1);
//     } else {
//       // Submit the final form
//       setSubmitted(true);
//     }
//   };

//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((step) => step - 1);
//   };

//   return (
//     // <div className="min-h-screen bg-slate-50 flex justify-center py-8">
//       <div className="w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
//         <Header />
//         <Stepper steps={steps} currentStep={currentStep} />

//         {submitted ? (
//           <SuccessSummary form={form} />
//         ) : (
//           <>
//             <FormBody
//               currentStep={currentStep}
//               form={form}
//               errors={errors}
//               updateField={updateField}
//             />
//             <Footer
//               currentStep={currentStep}
//               onBack={handleBack}
//               onNext={handleNext}
//               isLast={currentStep === steps.length}
//             />
//           </>
//         )}
//       </div>
//     // </div>
//   );
// }
